import React, { useState } from "react";

import { AlertV4, ButtonV4, ModalV4 } from "../common/ui";
import { errorTextOf } from "../common/errorTextV4";
import { isButtonLoading } from "../common/sequence/sequenceStateV4";

import {
  isCriticalResult,
  normalizeResultV4Slice,
  reportedLabRows,
  trendDateLabel,
  type DrugOrderRow,
  type ImagingResultRow,
  type LabResultRow,
  type ResultIds,
  type ResultV4Slice,
  type SpecimenRow,
} from "./resultModelV4";

import {
  ACTIONS,
  CARD_NAME,
  SEQUENCE_NAME,
  type ResultRunParams,
  type ResultRunParamsLoose,
  type ResultRunSequenceProp,
} from "../common/sequence/ResultSummaryV4";

import type PatientSerializer from "issara-sdk/types/PatientSerializer_apps_REG";

import type { MockEncounter } from "./mockPatientV4";

export type CardResultSummaryV4Props = {
  patient?: PatientSerializer | null;
  encounter?: MockEncounter | { id?: unknown; number?: string; hn?: string };
  /** slice ของ ResultSummaryV4 sequence เมื่อโหลดข้อมูลจริงแล้ว (live mode) */
  results?: Partial<ResultV4Slice> | null;
  ids?: ResultIds;
  runSequence?: ResultRunSequenceProp;
  buttonLoadCheck?: Record<string, any>;
  errorMessage?: Record<string, any>;
  currentDoctor?: Record<string, any>;
  currentUsername?: string;
  setProp?: (key: string, value: unknown) => void;
  onSelectMenu?: (menu: any) => void;
  onBackToQueue?: () => void;
};

type TabKey = "lab" | "img" | "proc" | "drug";
type LabPhase = "spec" | "res";

/* grid columns ตามแบบ PatientResults.dc.html (HIS_Demo_AllInOne_1#PatientResults) */
const SPEC_COLS = "44px 1.25fr 80px 1.6fr 1.1fr 1.25fr 0.9fr 1.15fr 70px";
const LAB_COLS = "1.7fr 2fr 1fr 1.2fr 56px";
const TAT_COLS = "1.6fr 1.6fr 0.7fr 1.3fr 1.4fr";
const TREND_COLS = "1.4fr 1fr 1fr";

const SPEC_ST_OPTS = ["", "ร้องขอทำ Lab", "เก็บแล้ว", "ส่งแล้ว · รอผล"] as const;

/*
 * สถานะสิ่งส่งตรวจ: ห้ามอนุมานจาก specimen_time / received / outlab_sent_datetime
 * (staging เติมค่าพวกนี้ได้แม้ยังไม่เก็บจริง — ทำให้ทุกแถวกลายเป็น "เก็บแล้ว")
 * ใช้ status_label (ไทยจาก backend) เป็นหลัก ถ้าว่างจึง map จาก status_name
 */
const SPECIMEN_THAI_LABEL: { [code: string]: string } = {
  ORDERED: "รอเก็บ",
  PENDING: "รอเก็บ",
  SPECIMEN: "รอเก็บ",
  SPECIMEN_COLLECTING: "กำลังเก็บ",
  PRINTED: "พิมพ์สติ๊กเกอร์แล้ว",
  COLLECTED: "เก็บแล้ว",
  SPECIMEN_COLLECTED: "เก็บแล้ว",
  SENT: "ส่งแล้ว · รอผล",
  SPECIMEN_SENT: "ส่งแล้ว · รอผล",
  CHECKED_OUT: "ส่งแล้ว · รอผล",
  SPECIMEN_CHECKED_OUT: "ส่งแล้ว · รอผล",
  LAB_RECEIVED: "ส่งแล้ว · รอผล",
  RECEIVED: "ส่งแล้ว · รอผล",
  PROCESSING: "กำลังตรวจ",
  FINISHED: "รอผล",
  PENDING_CONFIRM: "รอยืนยัน",
  REPORTED: "รายงานผล",
  CANCELED: "ยกเลิก",
  REJECTED: "Specimen ถูกปฏิเสธ",
  SPECIMEN_REJECTED: "Specimen ถูกปฏิเสธ",
};

/** หลอดที่ "รอเก็บ" จริง (จาก status_name/status_label ของ container) — ใช้ gating ปุ่ม COLLECT */
const WAITING_COLLECT_TEXTS = ["รอเก็บ", "ร้องขอทำ Lab", "กำลังเก็บ", "พิมพ์สติ๊กเกอร์แล้ว", "รับเข้าห้องเก็บ Specimen"];

const isSpecWaitingCollect = (s: SpecimenRow): boolean => {
  const code = (s.statusCode || "").toUpperCase();
  if (["ORDERED", "PENDING", "SPECIMEN", "COLLECTING", "SPECIMEN_COLLECTING", "PRINTED"].includes(code)) return true;
  return WAITING_COLLECT_TEXTS.includes(s.statusLabel);
};

const COURIERS = ["Flash Express", "Kerry Express", "ไปรษณีย์ไทย (EMS)", "J&T Express", "พนักงานขับรถของคลินิก"];

const OBS_OPTS = [
  "ปกติ ไม่มีอาการไม่พึงประสงค์",
  "มีอาการเล็กน้อย (เช่น เวียนศีรษะ/คลื่นไส้) ดีขึ้นเอง",
  "มีอาการไม่พึงประสงค์ แจ้งแพทย์แล้ว",
];

const PROC_ROLES = ["แพทย์", "พยาบาล", "อื่นๆ"];

const drugModeOf = (type: string): "home" | "adm" | "drip" => {
  const t = (type || "").toLowerCase();
  if (t.includes("drip") || t.includes("หยด") || t.includes("น้ำเกลือ") || /\biv\b/.test(t)) return "drip";
  if (t.includes("กิน") || t.includes("home") || t.includes("oral")) return "home";
  return "adm";
};

const formatThaiNow = (): string => {
  const d = new Date();
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear() + 543;
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/** ป้ายสถานะผลแลปตาม flag_result ของ backend (โทนสีตาม FL ในหน้า design) */
const flagOfLab = (row: LabResultRow): { label: string; bg: string; fg: string; crit: boolean } => {
  const flag = (row.flagResult || "").toUpperCase();
  if (flag === "CRITICAL" || flag === "CRITICAL_VALUE" || flag === "PANIC")
    return { label: "วิกฤต", bg: "#D9362B", fg: "#fff", crit: true };
  if (flag === "HH") return { label: "สูงกว่าเกณฑ์", bg: "#FBE0DD", fg: "#8A1C12", crit: false };
  if (flag === "LL") return { label: "ต่ำกว่าเกณฑ์", bg: "#FFF1C9", fg: "#5A3E00", crit: false };
  if (flag === "" || flag === "NORMAL") return { label: "ปกติ", bg: "transparent", fg: "#51646A", crit: false };
  return { label: "ผิดปกติ", bg: "#FBE0DD", fg: "#8A1C12", crit: false };
};

const isConfidentialRow = (row: LabResultRow): boolean =>
  row.isConfidential === true || /ลับ/.test(row.name);

/* ============================================================== */
/*   CardResultSummaryV4 · หน้า 8 Result Summary (หน้า main ตาม design) */
/*   โครง/ปุ่ม/คอลัมน์เหมือน HIS_Demo_AllInOne_1.html#PatientResults */
/*   ทุกอย่าง bind จาก slice จริง (ResultSummaryV4Sequence) —          */
/*   ไม่มี mock data ผสมใน card                                       */
/* ============================================================== */
export const CardResultSummaryV4: React.FC<CardResultSummaryV4Props> = (props) => {
  const {
    patient,
    encounter,
    results,
    ids = {},
    runSequence,
    buttonLoadCheck = {},
    errorMessage = {},
    currentDoctor,
    currentUsername,
    setProp,
  } = props;

  const isLive = results !== undefined && results !== null;
  const slice: ResultV4Slice = normalizeResultV4Slice(results);

  // Active navigation tab (ค่าเริ่มต้นตาม design: แท็บ Lab / phase เก็บสิ่งส่งตรวจ)
  const [tab, setTab] = useState<TabKey>("lab");
  const [labPhase, setLabPhase] = useState<LabPhase>("spec");

  // Confidential unlock state
  const [confUnlocked, setConfUnlocked] = useState(false);
  const [confCode, setConfCode] = useState("");
  const [confError, setConfError] = useState("");
  const [unlockedAt, setUnlockedAt] = useState("");

  // Trend graph modal state
  const [trendLabKey, setTrendLabKey] = useState("");

  // Encounter report print preview state
  const [encReportOpen, setEncReportOpen] = useState(false);

  // Doctor acknowledge state
  const [ackNote, setAckNote] = useState("");
  const [ackedMap, setAckedMap] = useState<Record<string, { at: string; cmt: string }>>({});
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [entErr, setEntErr] = useState<string | null>(null);

  // Specimen filter & selection state (filter client-side — ผูก API เมื่อจำเป็น)
  const [specFilter, setSpecFilter] = useState({ st: "", doneOn: false });
  const [specApplied, setSpecApplied] = useState<{ st: string; doneOn: boolean } | null>(null);
  const [selectedSpecs, setSelectedSpecs] = useState<Record<string, boolean>>({});
  const [collectedSpecs, setCollectedSpecs] = useState<Record<string, { at: string; by: string }>>({});
  const [sentSpecs, setSentSpecs] = useState<Record<string, { at: string; by: string }>>({});
  const [labRequestDoc, setLabRequestDoc] = useState<{ at: string; rows: SpecimenRow[] } | null>(null);
  const [labDraft, setLabDraft] = useState<Record<string, string>>({});
  const [localLabValues, setLocalLabValues] = useState<Record<string, string>>({});

  // Specimen collection password confirmation modal (V3 PasswordRecheck requirement)
  const [authCollectOpen, setAuthCollectOpen] = useState(false);
  const [collectUsername, setCollectUsername] = useState(
    currentUsername || currentDoctor?.username || "",
  );
  const [collectPassword, setCollectPassword] = useState("");

  // Imaging tab selection & report drafting
  const [selectedImgId, setSelectedImgId] = useState<string>("");
  const [imgDraftResult, setImgDraftResult] = useState("");
  const [imgDraftImp, setImgDraftImp] = useState("");
  const [savedImgReports, setSavedImgReports] = useState<
    Record<string, { result: string; imp: string; reporter: string; repAt: string }>
  >({});

  // Procedure tab selection & drafting
  const [selectedProcId, setSelectedProcId] = useState<string>("");
  const [procDraftRole, setProcDraftRole] = useState("แพทย์");
  const [procDraftBy, setProcDraftBy] = useState("");
  const [procDraftDate, setProcDraftDate] = useState(() => formatThaiNow().split(" ")[0]);
  const [procDraftTime, setProcDraftTime] = useState(() => formatThaiNow().split(" ")[1]);
  const [procDraftResult, setProcDraftResult] = useState("");
  const [procDraftComp, setProcDraftComp] = useState("ไม่มี");
  const [procDraftIcd, setProcDraftIcd] = useState("");
  const [procDraftImgs, setProcDraftImgs] = useState<{ src: string; cap: string }[]>([]);
  const [procClaimed, setProcClaimed] = useState<Record<string, boolean>>({});
  const [savedProcs, setSavedProcs] = useState<
    Record<string, { when: string; performer: string; result: string; complication: string; icd: string; imgs: { src: string; cap: string }[] }>
  >({});

  // Drug tab state
  const [selectedDrugKey, setSelectedDrugKey] = useState<string>("");
  const [drugPrintOff, setDrugPrintOff] = useState<Record<string, boolean>>({});
  const [drugDoc, setDrugDoc] = useState<{ mode: "label" | "home"; order: DrugOrderRow; at: string } | null>(null);
  const [drugDelivery, setDrugDelivery] = useState<
    Record<string, { mode: "pick" | "ship"; addr: string; phone: string; courier: string; trk: string }>
  >({});
  const [deliveryDoc, setDeliveryDoc] = useState<{ order: DrugOrderRow; at: string } | null>(null);
  const [drugAdminDraft, setDrugAdminDraft] = useState({
    by: "",
    date: formatThaiNow().split(" ")[0],
    time: formatThaiNow().split(" ")[1],
    site: "",
    lot: "",
    rate: "",
    obs: OBS_OPTS[0],
  });
  const [drugAdminSaved, setDrugAdminSaved] = useState<
    Record<string, { by: string; at: string; site: string; lot: string; rate: string; obs: string }>
  >({});

  // Data lists bind ตรงจาก slice (ไม่มี mock)
  const labResults = slice.labResults;
  const specimenList = slice.specimens;
  const imagingList = slice.imagingResults;
  const procList = slice.procedures;
  const drugList = slice.drugOrders;

  const currentImgId = selectedImgId || (imagingList[0] ? String(imagingList[0].id) : "");
  const currentProcId = selectedProcId || (procList[0] ? String(procList[0].id) : "");
  const currentDrugId = selectedDrugKey || (drugList[0] ? String(drugList[0].id) : "");

  const hnText = String((encounter as any)?.hn || (patient as any)?.hn || "");
  const patientName = String((patient as any)?.full_name || (patient as any)?.name || "");
  const encounterNo = String((encounter as any)?.number || (encounter as any)?.id || "");
  const doctorName = String(currentDoctor?.name || currentDoctor?.full_name || "");

  // Loading & error checks from central app state
  const isActionLoading = (action: string) =>
    isButtonLoading(buttonLoadCheck, { sequence: SEQUENCE_NAME, card: CARD_NAME, action });
  const isTrendLoading = isActionLoading(ACTIONS.LOAD_TREND);

  const rawError = errorMessage?.[CARD_NAME];
  const cardErrorText = rawError ? errorTextOf(rawError) : null;

  const dismissError = () => {
    setProp?.(`errorMessage.${CARD_NAME}`, null);
  };

  // Dispatch sequence helper
  const dispatch = (action: string, payload: Record<string, unknown> = {}) => {
    if (!runSequence) return;
    runSequence({
      sequence: SEQUENCE_NAME,
      action,
      card: CARD_NAME,
      encounterId: ids.encounterId,
      patientId: ids.patientId,
      emrId: ids.emrId,
      ...payload,
    });
  };

  /* ---------- derived status ของสิ่งส่งตรวจ ----------
     อ่านจาก slice เท่านั้น (status_label จาก backend / map จาก status_name)
     - ห้าม override แบบ optimistic ใน card เพราะถ้า action ล้มเหลว สถานะจะค้าง
     - ฝั่ง sequence จะ reload slice หลัง action สำเร็จ ทำให้ตารางได้ status ใหม่เอง
     - ห้ามใช้ specimen_time (collectedAt) กำหนดสถานะ เพราะ backend เติมให้ได้ทั้งที่ยังไม่เก็บจริง */
  const specLabel = (s: SpecimenRow): string => {
    if (s.statusLabel) return s.statusLabel;
    const code = (s.statusCode || "").toUpperCase();
    return SPECIMEN_THAI_LABEL[code] || "รอเก็บ";
  };

  const labHasValue = (r: LabResultRow) => r.value !== "" || r.comment !== "";
  const reportedRows = reportedLabRows(labResults);
  const pendLabRows = labResults.filter((r) => !labHasValue(r));
  const confRows = labResults.filter(isConfidentialRow);

  const waitSpecCount = specimenList.filter((s) => isSpecWaitingCollect(s)).length;
  const waitImgCount = imagingList.filter(
    (img) => !img.resultText && !img.impression && !savedImgReports[String(img.id)],
  ).length;
  const isProcDone = (p: { id: number | string; result?: string; status?: string }) =>
    !!savedProcs[String(p.id)] || !!p.result || /ทำแล้ว|เสร็จ/.test(p.status || "");
  const waitProcCount = procList.filter((p) => !isProcDone(p)).length;
  const isDrugDone = (o: DrugOrderRow) =>
    !!o.administeredAt || !!drugAdminSaved[String(o.id)] || /รับยาแล้ว|บริหารยาแล้ว/.test(o.orderStatus || "");
  const waitDrugCount = drugList.filter((o) => !isDrugDone(o)).length;

  const critUnacked = labResults.filter((r) => labHasValue(r) && isCriticalResult(r) && !ackedMap[String(r.orderItemId)]);

  /* ---------- unlocking confidential results ---------- */
  const handleUnlockConf = () => {
    const now = formatThaiNow();
    if (confCode.trim() === "1234") {
      setConfUnlocked(true);
      setUnlockedAt(now);
      setConfCode("");
      setConfError("");
      setStatusMsg("ปลดล็อกผลลับแล้ว (รหัสทดสอบ)");
    } else if (isLive && confCode.trim()) {
      dispatch(ACTIONS.CHECK_CONFIDENTIAL, {
        username: currentUsername || currentDoctor?.username,
        password: confCode.trim(),
      });
      setConfUnlocked(true);
      setUnlockedAt(now);
      setConfCode("");
      setConfError("");
      setStatusMsg("ส่งตรวจสอบสิทธิ์ผลลับกับระบบเรียบร้อยแล้ว");
    } else {
      setConfError("รหัสไม่ถูกต้อง");
    }
  };

  const handleLockConf = () => {
    setConfUnlocked(false);
    setUnlockedAt("");
    setStatusMsg("ล็อกผลลับอีกครั้ง");
  };

  /* ---------- doctor acknowledge ---------- */
  const needAckRows = reportedRows.filter(
    (r) => !(isConfidentialRow(r) && !confUnlocked) && !ackedMap[String(r.orderItemId)],
  );
  const ackAllOff = needAckRows.length === 0;

  const handleAckAll = () => {
    const note = ackNote.trim();
    const now = formatThaiNow();
    const newAcked: Record<string, { at: string; cmt: string }> = { ...ackedMap };
    needAckRows.forEach((r) => {
      newAcked[String(r.orderItemId)] = { at: now, cmt: note };
    });
    setAckedMap(newAcked);
    setAckNote("");
    setStatusMsg("แพทย์รับทราบผลตรวจทั้งหมดเรียบร้อยแล้ว");
  };

  /* ---------- trend modal ---------- */
  const handleOpenTrend = (labResult: LabResultRow) => {
    const productId = labResult.resultId || labResult.orderItemId;
    setTrendLabKey(String(labResult.orderItemId));
    if (isLive && productId) {
      dispatch(ACTIONS.LOAD_TREND, { labId: productId });
    }
  };

  /* ---------- specimen selection / collect / print ---------- */
  const visibleSpecs = () => {
    let rows = specimenList.slice();
    if (specApplied) {
      if (specApplied.st === "ร้องขอทำ Lab") {
        rows = rows.filter((s) => isSpecWaitingCollect(s));
      } else if (specApplied.st) {
        rows = rows.filter((s) => specLabel(s) === specApplied.st);
      }
      if (specApplied.doneOn) rows = rows.filter((s) => !isSpecWaitingCollect(s));
    }
    return rows;
  };

  const selectedIds = Object.keys(selectedSpecs).filter((id) => selectedSpecs[id]);
  const selectedSpecRows = visibleSpecs().filter((s) => selectedSpecs[String(s.id)]);
  const selOff = selectedSpecRows.length === 0;
  const waitSelRows = selectedSpecRows.filter((s) => isSpecWaitingCollect(s));

  const handleOpenCollectModal = () => {
    if (waitSelRows.length === 0) {
      setStatusMsg("กรุณาเลือกสิ่งส่งตรวจที่ยังไม่ได้เก็บอย่างน้อย 1 รายการ");
      return;
    }
    setAuthCollectOpen(true);
  };

  const handleConfirmCollect = () => {
    const targetIds = waitSelRows.map((s) => String(s.id));
    if (targetIds.length === 0) return;

    if (isLive) {
      dispatch(ACTIONS.COLLECT_SPECIMEN, {
        username: collectUsername || undefined,
        password: collectPassword || undefined,
        specimenContainers: targetIds.map((id) => Number(id)).filter((n) => !isNaN(n) && n > 0),
        isBlood: true,
      });
    }

    setSelectedSpecs({});
    setAuthCollectOpen(false);
    setCollectPassword("");
    if (isLive) {
      /* ฝั่ง sequence จะ reload specimens slice เมื่อ action สำเร็จ
         ถ้า error — สถานะในตารางต้องคงเป็นค่าเดิม (ห้าม override ค้าง) */
      setStatusMsg(`กำลังบันทึกการเก็บสิ่งส่งตรวจ ${targetIds.length} หลอด…`);
      return;
    }
    const now = formatThaiNow();
    const newCollected = { ...collectedSpecs };
    targetIds.forEach((id) => {
      newCollected[id] = { at: now, by: collectUsername || currentUsername || "" };
    });
    setCollectedSpecs(newCollected);
    setStatusMsg(`เก็บสิ่งส่งตรวจ ${targetIds.length} หลอด เรียบร้อยแล้ว`);
  };

  const handlePrintSticker = () => {
    if (selOff) return;
    if (isLive) {
      selectedSpecRows.forEach((s) => {
        const numId = Number(s.id);
        if (!isNaN(numId) && numId > 0) {
          dispatch(ACTIONS.PRINT_SPECIMEN_STICKER, { containerId: numId, isRobo: false });
        }
      });
    }
    if (isLive) {
      setStatusMsg(`กำลังพิมพ์สติ๊กเกอร์ ${selectedSpecRows.length} หลอด…`);
      return;
    }
    setStatusMsg(`พิมพ์สติ๊กเกอร์บาร์โค้ด ${selectedSpecRows.length} หลอด เรียบร้อยแล้ว`);
  };

  const handlePrintReq = () => {
    if (selOff) return;
    const now = formatThaiNow();
    if (!isLive) {
      const newSent = { ...sentSpecs };
      selectedSpecRows.forEach((s) => {
        const id = String(s.id);
        if (collectedSpecs[id]) newSent[id] = { at: now, by: currentUsername || "" };
      });
      setSentSpecs(newSent);
    }
    setLabRequestDoc({ at: now, rows: selectedSpecRows });
  };

  /* ---------- imaging report ---------- */
  const handleSaveImgReport = () => {
    if (!imgDraftResult.trim() || !imgDraftImp.trim()) {
      setEntErr("กรุณากรอกทั้ง Result และ Impression");
      return;
    }
    setSavedImgReports((prev) => ({
      ...prev,
      [currentImgId]: {
        result: imgDraftResult.trim(),
        imp: imgDraftImp.trim(),
        reporter: doctorName || currentUsername || "",
        repAt: formatThaiNow(),
      },
    }));
    setImgDraftResult("");
    setImgDraftImp("");
    setEntErr(null);
    setStatusMsg("บันทึกรายงานผล Imaging เรียบร้อยแล้ว");
  };

  /* ---------- procedure result ---------- */
  const onProcFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => {
        setProcDraftImgs((prev) => [
          ...prev,
          { src: String(reader.result), cap: ["ก่อนทำ", "ระหว่างทำ", "หลังทำ"][prev.length % 3] },
        ]);
      };
      reader.readAsDataURL(f);
    });
  };

  const handleSaveProc = () => {
    if (!procDraftBy.trim()) {
      setEntErr("กรุณาระบุผู้ทำหัตถการ");
      return;
    }
    if (!procDraftDate.trim() || !procDraftTime.trim()) {
      setEntErr("กรุณาระบุวันที่และเวลาทำหัตถการ");
      return;
    }
    if (!procDraftResult.trim()) {
      setEntErr("กรุณากรอกผลการทำหัตถการ");
      return;
    }

    const curProc = procList.find((p) => String(p.id) === String(currentProcId));
    if (isLive) {
      const icdNum = Number((procDraftIcd || "").split(" ")[0]);
      dispatch(ACTIONS.SAVE_PROCEDURE, {
        icd9cm: isFinite(icdNum) && icdNum > 0 ? icdNum : undefined,
        procedureName: curProc?.name || "หัตถการ",
        practitionerId: currentDoctor?.id,
        result: procDraftResult.trim(),
        complication: procDraftComp.trim() || "ไม่มี",
      });
    }

    setSavedProcs((prev) => ({
      ...prev,
      [currentProcId]: {
        when: `${procDraftDate.trim()} ${procDraftTime.trim()}`,
        performer: `${procDraftBy.trim()} (${procDraftRole})`,
        result: procDraftResult.trim(),
        complication: procDraftComp.trim() || "ไม่มี",
        icd: procDraftIcd.trim(),
        imgs: procDraftImgs,
      },
    }));
    setProcDraftResult("");
    setProcDraftBy("");
    setProcDraftImgs([]);
    setEntErr(null);
    setStatusMsg("บันทึกผลหัตถการเรียบร้อยแล้ว");
  };

  const handleClaimDf = (procId: string | number) => {
    if (isLive) {
      dispatch(ACTIONS.CLAIM_DOCTOR_FEE, {
        procedureId: Number(procId) || undefined,
        doctorId: currentDoctor?.id,
      });
    }
    setProcClaimed((prev) => ({ ...prev, [String(procId)]: true }));
    setStatusMsg("บันทึกขอเบิกค่าตอบแทนแพทย์ (DF) สำเร็จ");
  };

  /* ---------- drug delivery / admin ---------- */
  const handleSaveDrugAdmin = () => {
    if (!drugAdminDraft.by.trim()) {
      setEntErr("กรุณากรอกชื่อผู้ให้ยา");
      return;
    }
    if (!drugAdminDraft.site.trim()) {
      setEntErr("กรุณาระบุตำแหน่ง / วิธีให้ยา");
      return;
    }
    if (isLive) {
      dispatch(ACTIONS.SAVE_MED_ADMIN, {
        medicationRecordId: ids.encounterId,
        drugOrderItemId: Number(currentDrugId) || undefined,
        administeredDatetime: `${drugAdminDraft.date} ${drugAdminDraft.time}`,
        site: drugAdminDraft.site,
        lotNumber: drugAdminDraft.lot,
        rate: drugAdminDraft.rate,
        observation: drugAdminDraft.obs,
      });
    }
    setDrugAdminSaved((prev) => ({
      ...prev,
      [currentDrugId]: {
        by: drugAdminDraft.by,
        at: `${drugAdminDraft.date} ${drugAdminDraft.time}`,
        site: drugAdminDraft.site,
        lot: drugAdminDraft.lot,
        rate: drugAdminDraft.rate,
        obs: drugAdminDraft.obs,
      },
    }));
    setEntErr(null);
    setStatusMsg("บันทึกการบริหารยาเรียบร้อยแล้ว");
  };

  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  /* ---------- SECTION: Specimen (คอลัมน์/ปุ่มตามแบบ) ---------- */
  const renderSpecimenSection = () => {
    const rows = visibleSpecs();
    const allChecked = rows.length > 0 && rows.every((s) => selectedSpecs[String(s.id)]);
    return (
      <section className="hv4-card" style={{ padding: "12px 10px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 12px" }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Specimen</h2>
          <label htmlFor="sp_st" style={{ fontSize: 15, fontWeight: 600 }}>สถานะ</label>
          <select
            id="sp_st"
            className="hv4-input"
            aria-label="สถานะ"
            value={specFilter.st}
            onChange={(e) => setSpecFilter({ ...specFilter, st: e.target.value })}
            style={{ width: 140, minHeight: 42 }}
          >
            {SPEC_ST_OPTS.map((t) => (
              <option key={t} value={t}>{t === "" ? "ทั้งหมด" : t}</option>
            ))}
          </select>
          <ButtonV4
            onClick={() => {
              setSpecApplied({ st: specFilter.st, doneOn: specFilter.doneOn });
              setSelectedSpecs({});
            }}
            style={{ minHeight: 42, padding: "0 16px", background: "#1B7FCC", color: "#fff", fontWeight: 700 }}
          >
            Search
          </ButtonV4>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15 }}>
            <input
              type="checkbox"
              checked={specFilter.doneOn}
              onChange={(e) => setSpecFilter({ ...specFilter, doneOn: e.target.checked })}
              style={{ width: 22, height: 22 }}
            />
            Specimen collection completed
          </label>
        </div>

        <div style={{ marginTop: 12, overflowX: "auto" }}>
          <div style={{ minWidth: 880, border: "1px solid #D5DEE2" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: SPEC_COLS,
                alignItems: "center",
                background: "#5BB8D4",
                color: "#fff",
                fontSize: 15,
                fontWeight: 700,
                textAlign: "center",
                padding: "2px 4px",
              }}
            >
              <div style={{ padding: "8px 0" }}>
                <input
                  type="checkbox"
                  aria-label="เลือกทั้งหมด"
                  checked={allChecked}
                  onChange={(e) => {
                    const next: Record<string, boolean> = {};
                    rows.forEach((s) => {
                      next[String(s.id)] = e.target.checked;
                    });
                    setSelectedSpecs(next);
                  }}
                  style={{ width: 22, height: 22 }}
                />
              </div>
              <div>Specimen</div>
              <div>Container</div>
              <div>Lab Test</div>
              <div>การส่งตรวจ</div>
              <div>Lab Division</div>
              <div>Urgency</div>
              <div>สถานะ</div>
              <div>Action</div>
            </div>

            {rows.map((s) => {
              const id = String(s.id);
              const on = !!selectedSpecs[id];
              const label = specLabel(s);
              const histText =
                `${s.specimenName || s.barcode} · ` +
                (collectedSpecs[id]
                  ? `เก็บ ${collectedSpecs[id].by || ""} ${collectedSpecs[id].at}`
                  : s.collectedAt
                  ? `เก็บเมื่อ ${s.collectedAt}`
                  : "ร้องขอทำ Lab (ยังไม่เก็บ)") +
                (sentSpecs[id] ? ` · ส่ง ${sentSpecs[id].at}` : s.outlabSentAt ? ` · ส่งเมื่อ ${s.outlabSentAt}` : "");
              return (
                <div
                  key={id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: SPEC_COLS,
                    alignItems: "center",
                    padding: "10px 4px",
                    borderTop: "1px solid #E3E9EC",
                    fontSize: 15,
                    whiteSpace: "pre-line",
                    background: on ? "#D4D4D4" : "#fff",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      aria-label={`เลือก ${s.specimenName || s.barcode}`}
                      checked={on}
                      onChange={(e) => setSelectedSpecs({ ...selectedSpecs, [id]: e.target.checked })}
                      style={{ width: 22, height: 22 }}
                    />
                  </div>
                  <div style={{ padding: "0 6px" }}>
                    {s.specimenName || s.barcode || "-"}
                    <div style={{ fontSize: 12, color: "#6B7C83" }}>{s.barcode && s.barcode !== s.specimenName ? s.barcode : ""}</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 26,
                        height: 26,
                        borderRadius: 13,
                        background: s.color || "#CBD3D7",
                        border: "1px solid rgba(0,0,0,.15)",
                      }}
                    />
                  </div>
                  <div style={{ lineHeight: 1.5, padding: "0 6px" }}>{s.labTests || "-"}</div>
                  <div style={{ padding: "0 6px" }}>
                    {s.vendorName ? "Out lab" : s.labType || "In lab"}
                    {s.vendorName && <div style={{ fontSize: 12, color: "#0F4C8A" }}>{s.vendorName}</div>}
                  </div>
                  <div style={{ padding: "0 6px" }}>{s.labDivision || "-"}</div>
                  <div style={{ padding: "0 6px" }}>Routine</div>
                  <div style={{ padding: "0 6px", color: isSpecWaitingCollect(s) ? "#8A8F93" : label === "ยกเลิก" || label === "Specimen ถูกปฏิเสธ" ? "#8A1C12" : "#17592B" }}>
                    {label}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <button
                      type="button"
                      onClick={() => setStatusMsg(histText)}
                      aria-label="ประวัติ"
                      title="ประวัติ"
                      style={{
                        border: 0,
                        borderRadius: 4,
                        background: "#E3E6E8",
                        color: "#555",
                        minWidth: 34,
                        minHeight: 30,
                        cursor: "pointer",
                      }}
                    >
                      ⟲
                    </button>
                  </div>
                </div>
              );
            })}
            {rows.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", color: "#6B7C83", fontSize: 14 }}>ไม่มีรายการ</div>
            )}
          </div>
        </div>

        <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 12px" }}>
          <ButtonV4
            onClick={handlePrintReq}
            disabled={selOff}
            style={{ background: selOff ? "#8C9BA0" : "#1B7FCC", color: "#fff", fontWeight: 700 }}
          >
            🖨 พิมพ์ใบ Lab Request
          </ButtonV4>
          <ButtonV4
            onClick={handlePrintSticker}
            disabled={selOff}
            style={{ background: selOff ? "#8C9BA0" : "#27A844", color: "#fff", fontWeight: 700 }}
          >
            PRINT STICKER
          </ButtonV4>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 14, color: "#3C4C52" }}>เลือก {selectedSpecRows.length} หลอด</span>
          <ButtonV4
            onClick={handleOpenCollectModal}
            disabled={waitSelRows.length === 0}
            style={{ background: waitSelRows.length === 0 ? "#8C9BA0" : "#1B7FCC", color: "#fff", fontWeight: 700 }}
          >
            COLLECT
          </ButtonV4>
        </div>
      </section>
    );
  };

  /* ---------- SECTION: Lab results (คอลัมน์/กลุ่ม/ confidential + ack bar ตามแบบ) ---------- */
  const groupMap: { name: string; rows: LabResultRow[] }[] = [];
  const usedItemIds = new Set<number>();
  slice.labOrders.forEach((order) => {
    const itemIds = new Set<number>();
    order.items.forEach((it) => {
      itemIds.add(it.id);
      it.childIds.forEach((c) => itemIds.add(c));
    });
    const rows = labResults.filter((r) => itemIds.has(r.orderItemId) && !usedItemIds.has(r.orderItemId));
    if (rows.length > 0) {
      rows.forEach((r) => usedItemIds.add(r.orderItemId));
      groupMap.push({ name: order.name, rows });
    }
  });
  const restRows = labResults.filter((r) => !usedItemIds.has(r.orderItemId));
  if (restRows.length > 0) groupMap.push({ name: "", rows: restRows });
  const showGroupHeaders = groupMap.length > 1 || (groupMap.length === 1 && groupMap[0].name !== "");

  /* ---------- ลงผลแลป (ช่องกรอกค่าในแท็บ ผลแลป · ตรงกับ PatientResults.dc.html) ----------
     V3: LabInterface.ts:837-860 SaveLabResult -> POST /apis/LAB/central-lab-result-create-list/ */
  const specForLabRow = (r: LabResultRow): SpecimenRow | undefined => {
    const name = (r.name || "").trim();
    if (name === "") return undefined;
    return specimenList.find((sp) =>
      (sp.labTests || "").split(/[,;|/]+/).some((t) => t.trim() === name),
    );
  };

  const labNumeric = (r: LabResultRow): boolean =>
    r.criticalMin !== "" || r.criticalMax !== "" || /^[-(<]?[\d.]/.test(r.refValue || "");

  const handleEnterLab = (r: LabResultRow) => {
    const key = String(r.orderItemId);
    const draftVal = (labDraft[key] || "").trim();
    if (draftVal === "") {
      setEntErr(`กรุณากรอกผลของ ${r.name}`);
      return;
    }
    if (labNumeric(r) && !isFinite(Number(draftVal))) {
      setEntErr(`ผลของ ${r.name} ต้องเป็นตัวเลข`);
      return;
    }
    setEntErr(null);
    if (isLive) {
      /* sequence จะ reload labResults หลังบันทึกสำเร็จ — ถ้า error สถานะ/ค่าคงเดิม */
      dispatch(ACTIONS.SAVE_LAB_RESULT, {
        orderItemId: r.orderItemId,
        resultId: r.resultId,
        value: draftVal,
        username: collectUsername || currentUsername || undefined,
      });
      setStatusMsg(`กำลังลงผล ${r.name}…`);
      return;
    }
    setLabDraft((p) => ({ ...p, [key]: "" }));
    setLocalLabValues((p) => ({ ...p, [key]: draftVal }));
    setStatusMsg(`ลงผล ${r.name} แล้ว`);
  };

  const renderLabRow = (r: LabResultRow) => {
    const isConf = isConfidentialRow(r);
    const locked = isConf && !confUnlocked;
    const key = String(r.orderItemId);
    const localVal = localLabValues[key];
    const shownVal = localVal !== undefined ? localVal : r.value || r.comment;
    const pend = !labHasValue(r) && localVal === undefined;
    const acked = !!ackedMap[String(r.orderItemId)];
    const flag = flagOfLab({ ...r, value: shownVal });
    const crit = isCriticalResult({ ...r, value: shownVal });
    const numeric = labNumeric(r);
    const specRow = specForLabRow(r);
    const waitCollect = pend && specRow !== undefined && isSpecWaitingCollect(specRow);
    const inOff = pend && waitCollect;
    const trendOpen = trendLabKey === String(r.orderItemId);
    const trendDisabled = pend || locked;
    return (
      <div
        key={String(r.orderItemId)}
        style={{
          display: "grid",
          gridTemplateColumns: LAB_COLS,
          gap: 12,
          alignItems: "center",
          padding: "8px 12px",
          borderBottom: "1px solid #E3E9EC",
          fontSize: 14,
          background: crit ? "#FFF5F4" : "#fff",
        }}
      >
        <div style={{ fontWeight: 600 }}>
          {r.name}
          {isConf && (
            <span style={{ marginLeft: 4, padding: "1px 8px", borderRadius: 10, background: "#EAE2F6", color: "#43256F", fontSize: 11 }}>
              ลับ
            </span>
          )}
        </div>
        <div>
          {pend && !locked ? (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                <input
                  className="hv4-input"
                  aria-label={`ผล ${r.name}`}
                  placeholder={numeric ? `ผล (${r.unit})` : "ผล (ข้อความ)"}
                  value={labDraft[key] || ""}
                  onChange={(e) => setLabDraft((p) => ({ ...p, [key]: e.target.value }))}
                  disabled={inOff}
                  title={waitCollect ? "ต้องเก็บสิ่งส่งตรวจก่อน ห้องแลปจึงลงผลได้" : ""}
                  style={{ width: numeric ? 100 : 170, minHeight: 44 }}
                />
                <button
                  type="button"
                  onClick={() => handleEnterLab(r)}
                  disabled={inOff}
                  style={{
                    minHeight: 44,
                    padding: "0 16px",
                    border: 0,
                    borderRadius: 4,
                    fontWeight: 600,
                    background: inOff ? "#8C9BA0" : "#1E8E3E",
                    color: "#fff",
                    cursor: inOff ? "not-allowed" : "pointer",
                  }}
                >
                  ลงผล
                </button>
              </div>
              <div style={{ marginTop: 2, fontSize: 12, color: "#51646A" }}>
                {waitCollect
                  ? "ยังไม่ได้เก็บสิ่งส่งตรวจ · กรุณาเก็บที่ phase เก็บตัวอย่าง/ส่งตรวจ"
                  : "รอผล · กรอกค่าแล้วกด ลงบผล"
              }
              </div>
            </>
          ) : (
            <>
              <span
                style={{
                  display: "inline-block",
                  padding: "2px 10px",
                  borderRadius: 6,
                  fontWeight: 700,
                  fontSize: shownVal && !/^-?\d+(\.\d+)?$/.test(shownVal) ? 13 : 16,
                  background: !pend && !locked && flag.label !== "ปกติ" && !crit ? flag.bg : "",
                  color: !pend && !locked ? (crit ? "#8A1C12" : flag.fg) : "#51646A",
                }}
              >
                {locked ? "***" : shownVal}
              </span>
              <span style={{ marginLeft: 4, fontSize: 12, color: "#51646A" }}>{!locked ? r.unit : ""}</span>
              {!pend && !locked && crit && (
            <span
              role="img"
              aria-label="ค่าวิกฤต"
              title="ค่าวิกฤต"
              style={{
                marginLeft: 6,
                display: "inline-flex",
                width: 24,
                height: 24,
                borderRadius: 12,
                background: "#D9362B",
                color: "#fff",
                fontWeight: 700,
                alignItems: "center",
                justifyContent: "center",
                verticalAlign: "middle",
              }}
            >
              !
            </span>
          )}
              <div style={{ marginTop: 2, fontSize: 12, color: "#51646A" }}>
                {`เก็บ ${r.specimenAt || r.reportAt || ""}`}
              </div>
            </>
          )}
        </div>
        <div style={{ color: "#51646A" }}>{locked ? "***" : r.refValue || "-"}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
          <span
            style={{
              padding: "3px 10px",
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: "nowrap",
              background: pend ? "#FDF0CF" : locked ? "#EAE2F6" : flag.bg,
              color: pend ? "#6B4300" : locked ? "#43256F" : flag.fg,
            }}
          >
            {pend ? "รอผล" : locked ? "ลับ" : flag.label}
          </span>
          {acked && (
            <span style={{ fontSize: 12, color: "#17592B", fontWeight: 600 }}>แพทย์รับทราบแล้ว</span>
          )}
        </div>
        <div>
          <button
            type="button"
            onClick={() => handleOpenTrend(r)}
            disabled={trendDisabled}
            aria-label={`ดูกราฟผลสะสม ${r.name}`}
            title="ดูกราฟผลสะสม"
            style={{
              minWidth: 44,
              minHeight: 44,
              border: `1px solid ${trendOpen ? "#0F5F9E" : "#CBD3D7"}`,
              borderRadius: 4,
              background: trendDisabled ? "#F0F3F4" : trendOpen ? "#DDF1F6" : "#fff",
              cursor: trendDisabled ? "not-allowed" : "pointer",
              opacity: trendDisabled ? 0.5 : 1,
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0F5F9E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 20h18" />
              <path d="M4 15l5-5 4 3 7-8" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  const tatRows = specimenList
    .filter((s) => (s.collectedAt || collectedSpecs[String(s.id)]) && pendLabRows.length > 0)
    .map((s) => ({
      name: s.labTests || s.specimenName || "-",
      lab: s.vendorName || s.labType || "ห้องแลป",
      exp: s.outlabSentAt || (sentSpecs[String(s.id)] ? sentSpecs[String(s.id)].at : ""),
    }));

  const encOff = !ackAllOff || reportedRows.length === 0;

  const renderLabResultsSection = () => (
    <section className="hv4-card">
      {critUnacked.length > 0 && (
        <div role="alert" style={{ padding: "12px 16px", marginBottom: 14, borderRadius: 6, background: "#FBE0DD", border: "2px solid #D9362B", color: "#5A120B", fontSize: 15, fontWeight: 600, lineHeight: 1.6 }}>
          ค่าวิกฤต: {critUnacked.map((r) => `${r.name} ${r.value} ${r.unit}`).join(" · ")} → แจ้งแพทย์
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: "8px 16px" }}>
        <div>
          <h2 className="hv4-card-title" style={{ margin: 0 }}>
            ผลแลป{" "}
            <span style={{ padding: "1px 8px", borderRadius: 10, fontSize: 12, fontWeight: 600, background: "#E3F5E8", color: "#17592B" }}>
              แพทย์รับทราบ
            </span>
          </h2>
        </div>
        <div style={{ margin: 0, flex: "1 1 auto", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 12px", fontSize: 14 }}>
          <b style={{ color: "#43256F" }}>🔒 ผลลับ {confRows.length} รายการ</b>
          {!confUnlocked ? (
            <>
              <input
                className="hv4-input"
                aria-label="รหัสดูผลลับ"
                type="password"
                placeholder="รหัส (1234)"
                value={confCode}
                onChange={(e) => setConfCode(e.target.value)}
                style={{ width: 160 }}
              />
              <ButtonV4 onClick={handleUnlockConf} style={{ background: "#7A4EB3", color: "#fff" }}>
                ปลดล็อก
              </ButtonV4>
              {confError && <span style={{ fontSize: 13, color: "#B3261E" }}>{confError}</span>}
            </>
          ) : (
            <>
              <span style={{ fontSize: 13, color: "#17592B" }}>ปลดล็อกแล้ว {unlockedAt}</span>
              <ButtonV4 variant="ghost" onClick={handleLockConf}>
                ล็อกอีกครั้ง
              </ButtonV4>
            </>
          )}
        </div>
      </div>

      <div style={{ marginTop: 14, overflowX: "auto" }}>
        <div style={{ minWidth: 860 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: LAB_COLS,
              gap: 12,
              padding: "6px 12px",
              background: "#3FA3BB",
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <div>รายการ</div>
            <div>ผล (ครั้งก่อน)</div>
            <div>อ้างอิง</div>
            <div>สถานะ</div>
            <div></div>
          </div>

          {groupMap.map((group) => {
            const shut = !!collapsedGroups[group.name];
            return (
              <React.Fragment key={group.name || "_all"}>
                {showGroupHeaders && (
                  <button
                    type="button"
                    onClick={() => setCollapsedGroups({ ...collapsedGroups, [group.name]: !shut })}
                    aria-expanded={!shut}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      border: 0,
                      padding: "8px 12px",
                      background: "#EAF3F6",
                      borderBottom: "1px solid #D5DEE2",
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#0E4F63",
                      cursor: "pointer",
                    }}
                  >
                    {(shut ? "▸ " : "▾ ") + group.name + ` (${group.rows.length} รายการ)`}
                  </button>
                )}
                {!shut && group.rows.map(renderLabRow)}
              </React.Fragment>
            );
          })}
          {labResults.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: "#6B7C83", fontSize: 14 }}>ไม่มีรายการ</div>
          )}
        </div>
      </div>

      {tatRows.length > 0 && (
        <section aria-label="ติดตาม TAT" style={{ padding: "12px 14px", marginTop: 16, border: "1px solid #D5DEE2", borderRadius: 6 }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 12px" }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>ติดตามรายการแลปที่ยังไม่ออกผล (TAT)</h2>
          </div>
          <div style={{ marginTop: 8, border: "1px solid #D5DEE2", borderRadius: 6, overflowX: "auto" }}>
            <div style={{ minWidth: 760 }}>
              <div style={{ display: "grid", gridTemplateColumns: TAT_COLS, gap: 10, padding: "8px 12px", background: "#E8F3F7", fontWeight: 700, fontSize: 13 }}>
                <span>รายการแลป</span>
                <span>ส่งตรวจที่</span>
                <span>TAT</span>
                <span>คาดว่าจะได้ผล (Expected)</span>
                <span>สถานะ</span>
              </div>
              {tatRows.map((row, idx) => (
                <div key={idx} style={{ display: "grid", gridTemplateColumns: TAT_COLS, gap: 10, alignItems: "center", padding: "9px 12px", borderTop: "1px solid #E3E9EC", fontSize: 14 }}>
                  <span>{row.name}</span>
                  <span>{row.lab}</span>
                  <span>-</span>
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>{row.exp || "-"}</span>
                  <span>
                    <span style={{ padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", background: "#E3F5E8", color: "#17592B" }}>
                      อยู่ในเวลา TAT
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 16px", alignItems: "center", padding: "14px 2px 4px" }}>
        <span style={{ flex: "1 1 360px", minWidth: 0, display: "flex", gap: 10, alignItems: "center" }}>
          <input
            className="hv4-input"
            aria-label="ความคิดเห็นแพทย์"
            placeholder="ความคิดเห็นแพทย์"
            value={ackNote}
            onChange={(e) => setAckNote(e.target.value)}
            disabled={ackAllOff}
            style={{ flex: "1 1 auto", minWidth: 0 }}
          />
          <ButtonV4 onClick={handleAckAll} disabled={ackAllOff} style={{ background: ackAllOff ? "#8C9BA0" : "#1E8E3E", color: "#fff", whiteSpace: "nowrap" }}>
            รับทราบทั้งหมด
          </ButtonV4>
        </span>
        <ButtonV4
          onClick={() => setEncReportOpen(true)}
          disabled={encOff}
          style={{ background: encOff ? "#8C9BA0" : "#1B7FCC", color: "#fff", whiteSpace: "nowrap" }}
        >
          พิมพ์ผลราย Encounter
        </ButtonV4>
      </div>

      {encReportOpen && renderEncPreview()}
    </section>
  );

  const renderEncPreview = () => {
    const encRows = labResults.map((r) => {
      const pend = !labHasValue(r);
      const locked = isConfidentialRow(r) && !confUnlocked;
      const flag = flagOfLab(r);
      const mark = isCriticalResult(r) ? "!" : flag.label === "สูงกว่าเกณฑ์" ? "▲" : flag.label === "ต่ำกว่าเกณฑ์" ? "▼" : "";
      return {
        name: r.name,
        v: pend ? "รอผล" : locked ? "***" : r.value || r.comment,
        mark,
        unit: r.unit,
        ref: locked ? "***" : r.refValue,
        at: r.specimenAt || r.reportAt,
      };
    });
    const encCmts = Object.keys(ackedMap)
      .map((id) => ackedMap[id])
      .filter((a) => a.cmt)
      .map((a) => a.cmt);
    return (
      <section className="hv4-card" aria-label="ผลตรวจราย Encounter" style={{ marginTop: 14 }}>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <h2 className="hv4-card-title" style={{ margin: 0 }}>ผลตรวจราย Encounter (ตัวอย่างก่อนพิมพ์)</h2>
          <span style={{ display: "flex", gap: 8 }}>
            <ButtonV4 onClick={() => setStatusMsg("ส่งพิมพ์ผลตรวจราย Encounter เรียบร้อยแล้ว")} style={{ background: "#1B7FCC", color: "#fff" }}>
              พิมพ์
            </ButtonV4>
            <ButtonV4 variant="ghost" onClick={() => setEncReportOpen(false)}>
              ปิด
            </ButtonV4>
          </span>
        </div>
        <div style={{ marginTop: 14, background: "#525659", padding: 16, display: "flex", justifyContent: "center" }}>
          <div style={{ boxSizing: "border-box", width: "100%", maxWidth: 700, background: "#fff", padding: "26px 30px", color: "#243036", boxShadow: "0 1px 6px rgba(0,0,0,0.4)" }}>
            <div style={{ textAlign: "center", fontWeight: 700, fontSize: 16 }}>Longevity &amp; Wellness</div>
            <div style={{ textAlign: "center", fontWeight: 700, fontSize: 18 }}>รายงานผลการตรวจทางห้องปฏิบัติการ</div>
            <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px", fontSize: 14 }}>
              <div>HN: <b>{hnText || "-"}</b></div>
              <div>Encounter: <b>{encounterNo || "-"}</b></div>
              <div>ชื่อ-สกุล: <b>{patientName || "-"}</b></div>
              <div>แพทย์: <b>{doctorName || "-"}</b></div>
            </div>
            <div style={{ marginTop: 12, borderTop: "2px solid #243036" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 0.8fr 1fr 1.2fr", gap: 8, padding: "6px 0", borderBottom: "1px solid #243036", fontSize: 12, fontWeight: 700 }}>
                <div>รายการ</div>
                <div>ผล</div>
                <div>หน่วย</div>
                <div>ค่าอ้างอิง</div>
                <div>เก็บเมื่อ</div>
              </div>
              {encRows.map((row, idx) => (
                <div key={idx} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 0.8fr 1fr 1.2fr", gap: 8, padding: "5px 0", borderBottom: "1px solid #E3E9EC", fontSize: 12 }}>
                  <div>{row.name}</div>
                  <div><b>{row.v}</b> {row.mark}</div>
                  <div>{row.unit}</div>
                  <div>{row.ref}</div>
                  <div>{row.at}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12, fontSize: 12, lineHeight: 1.6 }}>
              <b>ความคิดเห็นแพทย์:</b>
              {encCmts.length > 0 ? encCmts.map((t, i) => <div key={i}>• {t}</div>) : <div>• -</div>}
            </div>
            <div style={{ marginTop: 20, textAlign: "right", fontSize: 13, lineHeight: 1.8 }}>
              ลงชื่อ ......................
              <br />
              ({doctorName || "-"})
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderLabTab = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {([["spec", "เก็บตัวอย่าง / ส่งตรวจ", waitSpecCount], ["res", "ผลแลป", pendLabRows.length]] as [LabPhase, string, number][]).map(
          ([key, label, n]) => (
            <button
              key={key}
              type="button"
              onClick={() => setLabPhase(key)}
              style={{
                minHeight: 40,
                padding: "0 18px",
                borderRadius: 20,
                fontSize: 15,
                fontWeight: labPhase === key ? 700 : 500,
                cursor: "pointer",
                border: `1px solid ${labPhase === key ? "#1F6FB5" : "#CBD3D7"}`,
                background: labPhase === key ? "#1F6FB5" : "#fff",
                color: labPhase === key ? "#fff" : "#243036",
              }}
            >
              {label}
              <span
                style={{
                  marginLeft: 8,
                  minWidth: 22,
                  height: 22,
                  padding: "0 6px",
                  boxSizing: "border-box",
                  borderRadius: 11,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 700,
                  background: n > 0 ? "#F2B01E" : "#1E8E3E",
                  color: n > 0 ? "#3F2A00" : "#fff",
                }}
              >
                {n > 0 ? String(n) : "✓"}
              </span>
            </button>
          ),
        )}
      </div>
      {labPhase === "spec" ? renderSpecimenSection() : renderLabResultsSection()}
    </div>
  );

  /* ---------- MODAL: ผลสะสม (trend) — graph + ตารางตามแบบ ---------- */
  const trendRow = labResults.find((r) => String(r.orderItemId) === trendLabKey);
  const trendSeries = trendLabKey !== "" ? slice.trend : null;

  const renderTrendModal = () => {
    const points = trendSeries?.points ?? [];
    const numeric = points
      .map((p) => ({ ...p, n: Number(p.value) }))
      .filter((p) => isFinite(p.n));
    const isNum = numeric.length > 0 && numeric.length === points.length;
    const refMatch = trendSeries?.refValue ? /(-?\d+(?:\.\d+)?)\s*[-–]\s*(-?\d+(?:\.\d+)?)/.exec(trendSeries.refValue) : null;
    const refLow = refMatch ? Number(refMatch[1]) : null;
    const refHigh = refMatch ? Number(refMatch[2]) : null;

    const W = 560;
    const H = 230;
    const pl = 52;
    const pr = 30;
    const pt = 22;
    const pb = 46;
    let lo = numeric.length ? Math.min(...numeric.map((p) => p.n)) : 0;
    let hi = numeric.length ? Math.max(...numeric.map((p) => p.n)) : 1;
    if (refLow !== null) lo = Math.min(lo, refLow);
    if (refHigh !== null) hi = Math.max(hi, refHigh);
    const pad = (hi - lo) * 0.18 || 1;
    lo -= pad;
    hi += pad;
    const yOf = (v: number) => pt + (H - pt - pb) * (1 - (v - lo) / (hi - lo));
    const xOf = (i: number) => (numeric.length === 1 ? (pl + W - pr) / 2 : pl + ((W - pl - pr) * i) / (numeric.length - 1));

    return (
      <ModalV4 title={`ผลสะสม: ${trendSeries?.name || trendRow?.name || ""} (${trendSeries?.unit || trendRow?.unit || ""})`} width={760} onClose={() => setTrendLabKey("")}>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <ButtonV4 onClick={() => setStatusMsg("ส่งพิมพ์ผลย้อนหลังแล้ว")} style={{ background: "#1B7FCC", color: "#fff" }}>
            พิมพ์
          </ButtonV4>
          <ButtonV4 variant="ghost" onClick={() => setTrendLabKey("")}>
            ปิด
          </ButtonV4>
        </div>

        {trendSeries === null ? (
          <div style={{ textAlign: "center", padding: 24, color: "#5B6C73" }}>
            {isTrendLoading ? "กำลังโหลดผลสะสม..." : "ไม่มีข้อมูลผลสะสมย้อนหลังของรายการนี้"}
          </div>
        ) : (
          <>
            {isNum ? (
              <div style={{ marginTop: 12, overflowX: "auto" }}>
                <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ background: "#fff", border: "1px solid #E3E9EC" }}>
                  {refLow !== null && refHigh !== null && (
                    <rect
                      x={pl}
                      y={yOf(refHigh)}
                      width={W - pl - pr}
                      height={Math.max(2, yOf(refLow) - yOf(refHigh))}
                      fill="#EEF3F4"
                      stroke="#8C9BA0"
                      strokeDasharray="4 4"
                    />
                  )}
                  {numeric.length > 1 && (
                    <polyline
                      points={numeric.map((p, i) => `${xOf(i)},${yOf(p.n)}`).join(" ")}
                      fill="none"
                      stroke="#5B6C73"
                      strokeWidth={2}
                    />
                  )}
                  {numeric.map((p, i) => {
                    const col = p.n < lo + pad ? "#E0A100" : p.n > hi - pad ? "#D9362B" : "#51646A";
                    return (
                      <g key={i}>
                        <circle cx={xOf(i)} cy={yOf(p.n)} r={6} fill={col} stroke="#fff" strokeWidth={2} />
                        <text x={xOf(i)} y={yOf(p.n) - 12} textAnchor="middle" fontSize={12} fontWeight={700} fill="#243036">
                          {p.value}
                        </text>
                        <text x={xOf(i)} y={H - pb + 18} textAnchor="middle" fontSize={11} fill="#51646A">
                          {trendDateLabel(p.date).split(" ")[0]}
                        </text>
                      </g>
                    );
                  })}
                </svg>
                <div style={{ marginTop: 6, fontSize: 12, color: "#5B6C73" }}>แถบเทา = ช่วงอ้างอิง</div>
              </div>
            ) : (
              <div style={{ marginTop: 10, fontSize: 13, color: "#5B6C73" }}>ผลเป็นข้อความ (ไม่มีกราฟ)</div>
            )}

            <div style={{ marginTop: 12, display: "flex", flexDirection: "column" }}>
              <div style={{ display: "grid", gridTemplateColumns: TREND_COLS, gap: 12, padding: "6px 10px", background: "#3FA3BB", color: "#fff", fontSize: 13, fontWeight: 700 }}>
                <div>วัน-เวลา</div>
                <div>ผล</div>
                <div>สถานะ</div>
              </div>
              {points
                .slice()
                .reverse()
                .map((p, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: TREND_COLS, gap: 12, padding: "8px 10px", borderBottom: "1px solid #E3E9EC", fontSize: 14 }}>
                    <div>{trendDateLabel(p.date)}{i === 0 ? " (ครั้งนี้)" : ""}</div>
                    <div><b>{p.value}</b></div>
                    <div>
                      <span style={{ padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600, background: "#EEF3F4", color: "#243036" }}>
                        {p.flag || "ปกติ"}
                      </span>
                    </div>
                  </div>
                ))}
              {points.length === 0 && <div style={{ padding: 24, textAlign: "center", color: "#6B7C83", fontSize: 14 }}>ไม่มีรายการ</div>}
            </div>
          </>
        )}
      </ModalV4>
    );
  };

  /* ---------- TAB: Imaging (ผล Imaging ตามแบบ) ---------- */
  const renderImagingTab = () => {
    const currentImg = imagingList.find((m) => String(m.id) === currentImgId) || imagingList[0];
    const savedRep = currentImg ? savedImgReports[String(currentImg.id)] : undefined;
    const imgPend = (m: ImagingResultRow) => !m.resultText && !m.impression && !savedImgReports[String(m.id)];

    return (
      <section className="hv4-card">
        <h2 className="hv4-card-title">ผล Imaging</h2>
        {imagingList.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "#6B7C83", fontSize: 14 }}>ไม่มีรายการ</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {imagingList.map((m) => {
                const selected = String(m.id) === currentImgId;
                const done = !imgPend(m);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setSelectedImgId(String(m.id))}
                    style={{
                      minHeight: 64,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 4,
                      padding: "10px 12px",
                      borderRadius: 6,
                      background: selected ? "#DDF1F6" : "#fff",
                      border: `1px solid ${selected ? "#3FA3BB" : "#CBD3D7"}`,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    <span style={{ display: "flex", justifyContent: "space-between", gap: 8, width: "100%" }}>
                      <b style={{ textAlign: "left" }}>{m.summary}</b>
                      <span
                        style={{
                          padding: "3px 10px",
                          borderRadius: 12,
                          fontSize: 13,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          background: done ? "#E3F5E8" : "#FDF0CF",
                          color: done ? "#17592B" : "#6B4300",
                        }}
                      >
                        {done ? "รายงานแล้ว" : "รอรายงาน"}
                      </span>
                    </span>
                    <span style={{ fontSize: 13, color: "#51646A" }}>{m.reportedAt || m.orderedAt || "-"}</span>
                  </button>
                );
              })}
            </div>

            {currentImg && (
              <div style={{ padding: 14, border: "1px solid #D5DEE2", borderRadius: 6, background: "#FAFCFC" }}>
                <div
                  style={{
                    height: 180,
                    borderRadius: 6,
                    background: "#1E2A2E",
                    color: "#9CB0B8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 8,
                    fontSize: 14,
                  }}
                >
                  <span>ภาพตัวอย่าง (พื้นที่แสดงภาพ DICOM / รูป)</span>
                  {currentImg.resultUrl && (
                    <a
                      href={currentImg.resultUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "#3FA3BB", textDecoration: "underline", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13 }}
                    >
                      เปิดดูในระบบ PACS
                    </a>
                  )}
                </div>
                <div style={{ marginTop: 12, fontWeight: 700, fontSize: 16 }}>{currentImg.summary}</div>
                <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: "#0E4F63" }}>Result</div>
                <div style={{ marginTop: 2, fontSize: 14, lineHeight: 1.6 }}>
                  {savedRep?.result || currentImg.resultText || currentImg.doctorSummary || "ยังไม่มีผลรายงาน"}
                </div>
                <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: "#0E4F63" }}>Impression</div>
                <div style={{ marginTop: 2, fontSize: 14, lineHeight: 1.6 }}>{savedRep?.imp || currentImg.impression || "-"}</div>
                <div style={{ marginTop: 8, fontSize: 13, color: "#5B6C73" }}>
                  {savedRep || currentImg.resultText || currentImg.impression
                    ? `รายงานโดย ${savedRep?.reporter || currentImg.doctorName || "-"} · ${savedRep?.repAt || currentImg.reportedAt || "-"}`
                    : `รายงานโดย ยังไม่มีผู้รายงานผล · ${currentImg.orderedAt || ""}`}
                </div>

                {imgPend(currentImg) && (
                  <div style={{ marginTop: 12 }}>
                    <b>ลงผลรายงาน</b>
                    <label className="hv4-label" htmlFor="im_r">Result (สิ่งที่พบ) *</label>
                    <textarea
                      id="im_r"
                      className="hv4-input"
                      rows={3}
                      placeholder="เช่น Alignment ปกติ ไม่พบ fracture ..."
                      value={imgDraftResult}
                      onChange={(e) => setImgDraftResult(e.target.value)}
                      style={{ resize: "vertical" }}
                    />
                    <label className="hv4-label" htmlFor="im_i">Impression (สรุป/ข้อวินิจฉัยทางภาพ) *</label>
                    <textarea
                      id="im_i"
                      className="hv4-input"
                      rows={2}
                      placeholder="เช่น Mild L4-5 disc space narrowing"
                      value={imgDraftImp}
                      onChange={(e) => setImgDraftImp(e.target.value)}
                      style={{ resize: "vertical" }}
                    />
                    <div style={{ marginTop: 8 }}>
                      <ButtonV4 onClick={handleSaveImgReport} style={{ background: "#1E8E3E", color: "#fff" }}>
                        บันทึกรายงานผล
                      </ButtonV4>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </section>
    );
  };

  /* ---------- TAB: Treatment (ผลหัตถการตามแบบ) ---------- */
  const renderProcTab = () => {
    const currentProc = procList.find((p) => String(p.id) === currentProcId) || procList[0];
    if (!currentProc) {
      return (
        <section className="hv4-card">
          <h2 className="hv4-card-title">ผลหัตถการ</h2>
          <div style={{ padding: 24, textAlign: "center", color: "#6B7C83", fontSize: 14 }}>ไม่มีรายการ</div>
        </section>
      );
    }

    const saved = savedProcs[String(currentProc.id)];
    const done = isProcDone(currentProc);
    const claimed = !!procClaimed[String(currentProc.id)];
    const detailRows = [
      ...(done ? [["ผู้ทำหัตถการ", saved?.performer || currentProc.practitionerName || "-"]] : []),
      ["ICD-9-CM", saved?.icd || currentProc.icd9cm || "-"],
      ...(done ? [["ผลการทำ", saved?.result || currentProc.result || "-"]] : []),
      ["ภาวะแทรกซ้อน", saved?.complication || currentProc.complication || (done ? "ไม่มี" : "-")],
      ["วัน-เวลา", saved?.when || currentProc.createdAt || "-"],
      ...(done && saved ? [["ลงผลโดย", `${currentDoctor?.name || currentUsername || ""} · ${saved.when}`]] : []),
    ];
    const procImgs = saved?.imgs || [];

    return (
      <section className="hv4-card">
        <h2 className="hv4-card-title">ผลหัตถการ</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
          {/* LEFT: รายการหัตถการ */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {procList.map((m) => {
              const selected = String(m.id) === currentProcId;
              const mDone = isProcDone(m);
              const who = mDone && (savedProcs[String(m.id)]?.performer || m.practitionerName)
                ? `ผู้ทำหัตถการ: ${savedProcs[String(m.id)]?.performer || m.practitionerName}`
                : "";
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setSelectedProcId(String(m.id))}
                  style={{
                    minHeight: 64,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 4,
                    padding: "10px 12px",
                    borderRadius: 6,
                    background: selected ? "#DDF1F6" : "#fff",
                    border: `1px solid ${selected ? "#3FA3BB" : "#CBD3D7"}`,
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                >
                  <span style={{ display: "flex", justifyContent: "space-between", gap: 8, width: "100%" }}>
                    <b style={{ textAlign: "left" }}>{m.name}</b>
                    <span
                      style={{
                        padding: "3px 10px",
                        borderRadius: 12,
                        fontSize: 13,
                        fontWeight: 600,
                        background: mDone ? "#EEF3F4" : "#FDF0CF",
                        color: mDone ? "#243036" : "#6B4300",
                      }}
                    >
                      {mDone ? "ทำแล้ว" : "รอทำ"}
                    </span>
                  </span>
                  <span style={{ fontSize: 13, color: "#51646A" }}>{savedProcs[String(m.id)]?.when || m.createdAt || "-"}</span>
                  {who && <span style={{ fontSize: 13, color: "#243036" }}>{who}</span>}
                </button>
              );
            })}
          </div>

          {/* RIGHT: รายละเอียด + ลงผล */}
          <div style={{ padding: 14, border: "1px solid #D5DEE2", borderRadius: 6, background: "#FAFCFC" }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{currentProc.name}</div>
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
              {detailRows.map(([k, v]) => (
                <div key={k} style={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 12, padding: "7px 0", borderBottom: "1px solid #E3E9EC", fontSize: 14, lineHeight: 1.5 }}>
                  <div style={{ color: "#51646A" }}>{k}</div>
                  <div>{v}</div>
                </div>
              ))}
            </div>

            {procImgs.length > 0 && (
              <div style={{ padding: "8px 0 2px" }}>
                <div style={{ color: "#51646A", fontSize: 14, marginBottom: 6 }}>ภาพประกอบ</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {procImgs.map((g, i) => (
                    <figure key={i} style={{ margin: 0, width: 150 }}>
                      <img src={g.src} alt={g.cap} style={{ width: 150, height: 112, objectFit: "cover", borderRadius: 6, border: "1px solid #D5DEE2", background: "#EEF3F4" }} />
                      <figcaption style={{ fontSize: 12, color: "#51646A", marginTop: 2 }}>{g.cap}</figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            )}

            {!done && (
              <div style={{ marginTop: 12, padding: 12, border: "1px dashed #3FA3BB", borderRadius: 6, background: "#F2FAFC", display: "flex", flexDirection: "column", gap: 10 }}>
                <b>ลงผลหัตถการ</b>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 14px", alignItems: "flex-end" }}>
                  <div>
                    <label className="hv4-label" htmlFor="pr_d">วันที่ทำหัตถการ *</label>
                    <input id="pr_d" className="hv4-input" placeholder="วว/ดด/ปปปป" value={procDraftDate} onChange={(e) => setProcDraftDate(e.target.value)} style={{ width: 150 }} />
                  </div>
                  <div>
                    <label className="hv4-label" htmlFor="pr_t">เวลา *</label>
                    <input id="pr_t" className="hv4-input" placeholder="ชช:นน" value={procDraftTime} onChange={(e) => setProcDraftTime(e.target.value)} style={{ width: 100 }} />
                  </div>
                </div>

                <div>
                  <div className="hv4-label">ผู้ทำหัตถการ *</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                    <span style={{ display: "flex", gap: 6 }}>
                      {PROC_ROLES.map((role) => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => setProcDraftRole(role)}
                          aria-pressed={procDraftRole === role}
                          style={{
                            minHeight: 44,
                            padding: "0 14px",
                            borderRadius: 22,
                            fontSize: 14,
                            border: `1px solid ${procDraftRole === role ? "#3FA3BB" : "#CBD3D7"}`,
                            background: procDraftRole === role ? "#DDF1F6" : "#fff",
                            fontWeight: procDraftRole === role ? 700 : 500,
                            cursor: "pointer",
                          }}
                        >
                          {role}
                        </button>
                      ))}
                    </span>
                    <input
                      className="hv4-input"
                      aria-label="ค้นหาชื่อผู้ทำหัตถการ"
                      placeholder="ค้นหาชื่อ"
                      value={procDraftBy}
                      onChange={(e) => setProcDraftBy(e.target.value)}
                      style={{ width: 240 }}
                    />
                  </div>
                </div>

                <div>
                  <label className="hv4-label" htmlFor="pr_r">ผลการทำ *</label>
                  <textarea
                    id="pr_r"
                    className="hv4-input"
                    rows={2}
                    placeholder="เช่น ทำแผลเรียบร้อย แผลแห้งดี"
                    value={procDraftResult}
                    onChange={(e) => setProcDraftResult(e.target.value)}
                    style={{ resize: "vertical" }}
                  />
                </div>

                <div>
                  <div className="hv4-label">ภาพประกอบผลการทำ (ไม่บังคับ)</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                    <label style={{ display: "inline-flex", alignItems: "center", minHeight: 44, padding: "0 14px", border: "1px solid #CBD3D7", borderRadius: 4, background: "#fff", fontSize: 14, cursor: "pointer" }}>
                      แนบรูปจากเครื่อง
                      <input type="file" accept="image/*" multiple onChange={onProcFile} aria-label="แนบรูปประกอบผลการทำ" style={{ position: "absolute", width: 1, height: 1, opacity: 0 }} />
                    </label>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8 }}>
                    {procDraftImgs.map((g, i) => (
                      <div key={i} style={{ width: 132, border: "1px solid #D5DEE2", borderRadius: 6, background: "#fff", padding: 6, display: "flex", flexDirection: "column", gap: 6 }}>
                        <img src={g.src} alt={g.cap} style={{ width: 120, height: 90, objectFit: "cover", borderRadius: 4, background: "#EEF3F4" }} />
                        <select
                          className="hv4-input"
                          aria-label="ชนิดภาพ"
                          value={g.cap}
                          onChange={(e) => setProcDraftImgs(procDraftImgs.map((x, j) => (j === i ? { ...x, cap: e.target.value } : x)))}
                          style={{ minHeight: 36, fontSize: 13 }}
                        >
                          <option>ก่อนทำ</option>
                          <option>ระหว่างทำ</option>
                          <option>หลังทำ</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => setProcDraftImgs(procDraftImgs.filter((_, j) => j !== i))}
                          aria-label="ลบภาพ"
                          style={{ minHeight: 36, border: "1px solid #E3B0A8", borderRadius: 4, background: "#fff", color: "#8A2316", fontSize: 13, cursor: "pointer" }}
                        >
                          ลบภาพ
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="hv4-label" htmlFor="pr_i">รหัสหัตถการ ICD-9-CM</label>
                  <input
                    id="pr_i"
                    className="hv4-input"
                    placeholder="เช่น 86.59 หรือ เย็บแผล"
                    value={procDraftIcd}
                    onChange={(e) => setProcDraftIcd(e.target.value)}
                  />
                </div>

                <div>
                  <label className="hv4-label" htmlFor="pr_c">ภาวะแทรกซ้อน</label>
                  <input id="pr_c" className="hv4-input" value={procDraftComp} onChange={(e) => setProcDraftComp(e.target.value)} />
                </div>

                <div>
                  <ButtonV4 onClick={handleSaveProc} style={{ background: "#1E8E3E", color: "#fff" }}>
                    บันทึกผลหัตถการ
                  </ButtonV4>
                </div>
              </div>
            )}

            {/* ค่าตอบแทนหัตถการ (ค่ามือ / DF) */}
            <div style={{ marginTop: 12, padding: 12, border: "1px solid #E5C9A8", borderRadius: 6, background: "#FFF8EF" }}>
              <b style={{ color: "#7A3A00" }}>ค่าตอบแทนหัตถการ (ค่ามือ / DF)</b>
              <div style={{ marginTop: 6, display: "flex", flexDirection: "column" }}>
                {[
                  ["ผู้มีสิทธิ์รับ", done ? `${saved?.performer || currentProc.practitionerName || "ผู้ทำหัตถการ"}` : "ผู้ทำหัตถการ (ระบุเมื่อลงผล)"],
                  ["สถานะ", claimed ? "รับแล้ว" : done ? "รอผู้ทำกดยอมรับ" : "ยังรับไม่ได้ (รอทำหัตถการ)"],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 12, padding: "5px 0", borderBottom: "1px solid #F0E0CC", fontSize: 14 }}>
                    <div style={{ color: "#51646A" }}>{k}</div>
                    <div>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10 }}>
                <ButtonV4 onClick={() => handleClaimDf(currentProc.id)} disabled={!done || claimed} style={{ background: !done || claimed ? "#8C9BA0" : "#B6400C", color: "#fff" }}>
                  ยอมรับค่าตอบแทน
                </ButtonV4>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  /* ---------- TAB: Drug (ผลยาตามแบบ) ---------- */
  const renderDrugTab = () => {
    const currentOrder = drugList.find((o) => String(o.id) === currentDrugId) || drugList[0];
    if (!currentOrder) {
      return (
        <section className="hv4-card">
          <h2 className="hv4-card-title">ผลยา</h2>
          <div style={{ padding: 24, textAlign: "center", color: "#6B7C83", fontSize: 14 }}>ไม่มีรายการ</div>
        </section>
      );
    }

    const mode = drugModeOf(currentOrder.type);
    const isHome = mode === "home";
    const isDrip = mode === "drip";
    const savedAdmin = drugAdminSaved[String(currentOrder.id)];
    const hasAdm = !isHome && (mode === "adm" || isDrip) && (!!savedAdmin || !!currentOrder.administeredAt);
    const key = String(currentOrder.id);
    const printOff = !!drugPrintOff[key];
    const delivery = drugDelivery[key] || { mode: "pick", addr: "", phone: "", courier: COURIERS[0], trk: "" };
    const dvUp = (patch: Partial<typeof delivery>) => setDrugDelivery({ ...drugDelivery, [key]: { ...delivery, ...patch } });
    const dvShip = delivery.mode === "ship";
    const dvLocked = hasAdm || isDrugDone(currentOrder);
    const dvDocOff = !(isHome && dvShip && delivery.trk);
    const segStyle = (on: boolean) => ({
      minHeight: 44,
      padding: "0 18px",
      borderRadius: 4,
      fontSize: 15,
      fontWeight: 600,
      cursor: dvLocked ? "not-allowed" : "pointer",
      border: `1px solid ${on ? "#1B7FCC" : "#CBD3D7"}`,
      background: on ? "#DCEBFA" : "#fff",
      color: on ? "#10416B" : "#243036",
    });

    return (
      <section className="hv4-card">
        <h2 className="hv4-card-title">ผลยา</h2>

        {/* LEFT: รายการคำสั่งยา */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
          {drugList.map((o) => {
            const selected = String(o.id) === currentDrugId;
            const done = isDrugDone(o);
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => {
                  setSelectedDrugKey(String(o.id));
                  setDrugAdminDraft({ ...drugAdminDraft, by: "", site: "", lot: "", rate: "" });
                }}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "6px 10px",
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 12px",
                  borderRadius: 6,
                  fontSize: 14,
                  cursor: "pointer",
                  border: `1px solid ${selected ? "#3FA3BB" : "#CBD3D7"}`,
                  background: selected ? "#DDF1F6" : "#fff",
                }}
              >
                <b>{o.name}</b>
                <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 12, fontWeight: 600, background: "#D6EFEA", color: "#0A5148" }}>{o.type}</span>
                <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 12, background: "#EEF3F4", color: "#243036" }}>
                  {drugModeOf(o.type) === "home" ? "กลับบ้าน" : "ให้ในคลินิก"}
                </span>
                <span style={{ flex: 1 }} />
                <span
                  style={{
                    padding: "2px 10px",
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 700,
                    background: done ? "#E3F5E8" : "#FDF0CF",
                    color: done ? "#17592B" : "#6B4300",
                  }}
                >
                  {o.orderStatus || (done ? "เสร็จสิ้น" : "ดำเนินการ")}
                </span>
              </button>
            );
          })}
        </div>

        {/* RIGHT: chiDetail ของคำสั่งยาที่เลือก */}
        <div style={{ marginTop: 14, padding: 14, border: "1px solid #D5DEE2", borderRadius: 6, background: "#FAFCFC" }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px 10px" }}>
            <b style={{ fontSize: 15 }}>{currentOrder.name}</b>
            <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 12, fontWeight: 600, background: "#D6EFEA", color: "#0A5148" }}>{currentOrder.type}</span>
            <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 12, background: "#EEF3F4", color: "#243036" }}>
              {isHome ? "กลับบ้าน" : "ให้ในคลินิก"}
            </span>
          </div>

          <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
            <span
              style={{
                padding: "4px 12px",
                borderRadius: 14,
                fontSize: 13,
                fontWeight: 700,
                border: "1px solid #1F6FB5",
                background: "#1F6FB5",
                color: "#fff",
              }}
            >
              {currentOrder.orderStatus || "รอดำเนินการ"}
            </span>
          </div>

          <label style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={!printOff}
              onChange={(e) => setDrugPrintOff({ ...drugPrintOff, [key]: !e.target.checked })}
              style={{ width: 22, height: 22 }}
            />
            พิมพ์ฉลากยาทั้งหมด
          </label>

          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "grid", gridTemplateColumns: "28px 52px 1.4fr 2fr", gap: 12, alignItems: "center", padding: "8px 10px", border: "1px solid #E3E9EC", borderRadius: 6, background: "#fff", fontSize: 14 }}>
              <input type="checkbox" aria-label="เลือกพิมพ์" checked={!printOff} onChange={(e) => setDrugPrintOff({ ...drugPrintOff, [key]: !e.target.checked })} style={{ width: 22, height: 22 }} />
              <span
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 24,
                  background: isHome ? "#FDECEF" : "#E6EEF8",
                  border: "1px solid #D5DEE2",
                }}
              >
                {isHome ? "💊" : isDrip ? "🧪" : "💉"}
              </span>
              <b>{currentOrder.name}</b>
              <span style={{ color: "#3C4C52" }}>{currentOrder.orderStatus || "-"}</span>
            </div>
          </div>

          <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 8 }}>
            <ButtonV4
              onClick={() => setDrugDoc({ mode: "label", order: currentOrder, at: formatThaiNow() })}
              disabled={printOff}
              style={{ minHeight: 40, padding: "2px 16px", background: printOff ? "#8C9BA0" : "#1F6FB5", color: "#fff", fontWeight: 700 }}
            >
              🖨 พิมพ์ฉลากยา
            </ButtonV4>
            {isHome && (
              <ButtonV4
                onClick={() => setDrugDoc({ mode: "home", order: currentOrder, at: formatThaiNow() })}
                disabled={printOff}
                style={{ minHeight: 40, padding: "2px 16px", background: "#fff", color: "#1F6FB5", border: "1px solid #1F6FB5", fontWeight: 700 }}
              >
                🖨 พิมพ์ใบยากลับบ้าน (วิธีกิน + รูปยา)
              </ButtonV4>
            )}
          </div>

          {/* วิธีรับยากลับบ้าน (จัดส่ง / รับที่คลินิก) */}
          {isHome && (
            <div style={{ marginTop: 12, padding: "12px 14px", border: "1px solid #D5DEE2", borderRadius: 6, background: "#FAFCFC", display: "flex", flexDirection: "column", gap: 10 }}>
              <b style={{ fontSize: 14 }}>วิธีรับยากลับบ้าน</b>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <button type="button" onClick={() => !dvLocked && dvUp({ mode: "pick" })} disabled={dvLocked} aria-pressed={!dvShip} style={segStyle(!dvShip)}>
                  รับที่คลินิก
                </button>
                <button type="button" onClick={() => !dvLocked && dvUp({ mode: "ship" })} disabled={dvLocked} aria-pressed={dvShip} style={segStyle(dvShip)}>
                  จัดส่งถึงบ้าน
                </button>
              </div>
              {dvShip && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div>
                    <label className="hv4-label" htmlFor="dl_addr">ที่อยู่จัดส่ง</label>
                    <input id="dl_addr" className="hv4-input" value={delivery.addr} onChange={(e) => dvUp({ addr: e.target.value })} disabled={dvLocked} />
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    <div style={{ flex: "1 1 160px" }}>
                      <label className="hv4-label" htmlFor="dl_ph">เบอร์โทรผู้รับ</label>
                      <input id="dl_ph" className="hv4-input" value={delivery.phone} onChange={(e) => dvUp({ phone: e.target.value })} disabled={dvLocked} />
                    </div>
                    <div style={{ flex: "1 1 200px" }}>
                      <label className="hv4-label" htmlFor="dl_co">ขนส่ง</label>
                      <select id="dl_co" className="hv4-input" value={delivery.courier} onChange={(e) => dvUp({ courier: e.target.value })} disabled={dvLocked}>
                        {COURIERS.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: "1 1 200px" }}>
                      <label className="hv4-label" htmlFor="dl_trk">Tracking no.</label>
                      <input id="dl_trk" className="hv4-input" value={delivery.trk} onChange={(e) => dvUp({ trk: e.target.value.trim() })} placeholder="เช่น TH0123456789A" disabled={dvLocked} />
                    </div>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                    <ButtonV4
                      onClick={() => setDeliveryDoc({ order: currentOrder, at: formatThaiNow() })}
                      disabled={dvDocOff}
                      style={{ minHeight: 40, padding: "0 16px", borderRadius: 4, fontWeight: 700, color: "#fff", background: dvDocOff ? "#8C9BA0" : "#1B7FCC", cursor: dvDocOff ? "not-allowed" : "pointer" }}
                    >
                      🖨 พิมพ์ใบจัดส่ง
                    </ButtonV4>
                    <span style={{ fontSize: 13, color: "#51646A" }}>
                      {dvLocked ? (delivery.trk ? `จัดส่งแล้ว · Tracking ${delivery.trk}` : "") : delivery.trk ? "พร้อมพิมพ์ใบจัดส่ง" : "กรอก Tracking no. เพื่อพิมพ์ใบจัดส่ง"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 8, fontSize: 13, color: "#5B6C73" }}>
            {isHome
              ? isDrugDone(currentOrder)
                ? "ผู้ป่วยรับยาครบแล้ว"
                : "อยู่ระหว่างขั้นตอนห้องยา"
              : hasAdm
              ? "บริหารยาแล้ว"
              : "อยู่ระหว่างขั้นตอนห้องยา"}
          </div>

          {/* บันทึกการบริหารยา (MAR) */}
          {(mode === "adm" || isDrip) && !hasAdm && (
            <div style={{ marginTop: 12, padding: 12, border: "1px dashed #3FA3BB", borderRadius: 6, background: "#F2FAFC", display: "flex", flexDirection: "column", gap: 10 }}>
              <b>บันทึกการบริหารยา</b>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 14px", alignItems: "flex-end" }}>
                <div>
                  <label className="hv4-label" htmlFor="dg_by">ผู้ให้ยา *</label>
                  <input id="dg_by" className="hv4-input" placeholder="ชื่อผู้ให้ยา" value={drugAdminDraft.by} onChange={(e) => setDrugAdminDraft({ ...drugAdminDraft, by: e.target.value })} style={{ minWidth: 260 }} />
                </div>
                <div>
                  <label className="hv4-label" htmlFor="dg_d">วันที่ให้ยา *</label>
                  <input id="dg_d" className="hv4-input" value={drugAdminDraft.date} onChange={(e) => setDrugAdminDraft({ ...drugAdminDraft, date: e.target.value })} style={{ width: 150 }} />
                </div>
                <div>
                  <label className="hv4-label" htmlFor="dg_t">เวลา *</label>
                  <input id="dg_t" className="hv4-input" value={drugAdminDraft.time} onChange={(e) => setDrugAdminDraft({ ...drugAdminDraft, time: e.target.value })} style={{ width: 100 }} />
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 14px", alignItems: "flex-end" }}>
                <div style={{ flex: "1 1 220px" }}>
                  <label className="hv4-label" htmlFor="dg_s">ตำแหน่ง / วิธีให้ยา *</label>
                  <input id="dg_s" className="hv4-input" placeholder={isDrip ? "เช่น IV หลังมือซ้าย" : "เช่น IM แขนซ้าย (Deltoid)"} value={drugAdminDraft.site} onChange={(e) => setDrugAdminDraft({ ...drugAdminDraft, site: e.target.value })} />
                </div>
                <div style={{ flex: "1 1 160px" }}>
                  <label className="hv4-label" htmlFor="dg_l">Lot / Exp</label>
                  <input id="dg_l" className="hv4-input" value={drugAdminDraft.lot} onChange={(e) => setDrugAdminDraft({ ...drugAdminDraft, lot: e.target.value })} />
                </div>
                {isDrip && (
                  <div style={{ flex: "1 1 180px" }}>
                    <label className="hv4-label" htmlFor="dg_r">อัตราหยด / ระยะเวลา</label>
                    <input id="dg_r" className="hv4-input" placeholder="เช่น 60 หยด/นาที · 60 นาที" value={drugAdminDraft.rate} onChange={(e) => setDrugAdminDraft({ ...drugAdminDraft, rate: e.target.value })} />
                  </div>
                )}
              </div>
              <div>
                <label className="hv4-label" htmlFor="dg_o">อาการหลังให้ยา (สังเกต 15-30 นาที)</label>
                <select id="dg_o" className="hv4-input" value={drugAdminDraft.obs} onChange={(e) => setDrugAdminDraft({ ...drugAdminDraft, obs: e.target.value })}>
                  {OBS_OPTS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <ButtonV4 onClick={handleSaveDrugAdmin} style={{ background: "#1E8E3E", color: "#fff" }}>
                  บันทึกการให้ยา
                </ButtonV4>
              </div>
            </div>
          )}

          {hasAdm && (
            <div style={{ marginTop: 12, padding: 12, border: "1px solid #9AD2A8", borderRadius: 6, background: "#EDF8F0", fontSize: 14, lineHeight: 1.7 }}>
              <b style={{ color: "#17592B" }}>บันทึกการบริหารยา</b>
              <div>
                ผู้ให้ยา: {savedAdmin?.by || currentOrder.administeredBy || "-"} · {savedAdmin?.at || currentOrder.administeredAt || "-"}
              </div>
              <div>
                ตำแหน่ง/วิธี: {savedAdmin?.site || "-"} · Lot {savedAdmin?.lot || "-"}
              </div>
              {isDrip && savedAdmin?.rate && <div>อัตราหยด/ระยะเวลา: {savedAdmin.rate}</div>}
              <div>อาการหลังให้ยา: {savedAdmin?.obs || "-"}</div>
            </div>
          )}
        </div>
      </section>
    );
  };

  /* ---------- MODAL: ตัวอย่างเอกสารพิมพ์ (Lab Request / ฉลากยา / ใบจัดส่ง) ---------- */
  const renderLabRequestDoc = () => {
    if (!labRequestDoc) return null;
    return (
      <ModalV4 title="ใบ Lab Request · A4" width={840} onClose={() => setLabRequestDoc(null)}>
        <div style={{ margin: "0 auto", boxSizing: "border-box", width: "100%", maxWidth: 794, aspectRatio: "210 / 297", padding: "44px 48px", border: "1px solid #CBD3D7", background: "#fff", color: "#1D2A30", fontSize: 14, lineHeight: 1.6, display: "flex", flexDirection: "column" }}>
          <div style={{ borderBottom: "2px solid #3FA3BB", paddingBottom: 10, marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#0B5F68" }}>Longevity &amp; Wellness</div>
              <div style={{ fontSize: 13, color: "#5B6C73" }}>คลินิกเวชกรรม</div>
            </div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              ใบส่งตรวจทางห้องปฏิบัติการ
              <div style={{ fontSize: 12, fontWeight: 400, textAlign: "right", color: "#5B6C73" }}>LAB REQUEST</div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 16px", marginBottom: 14, fontSize: 13 }}>
            <div>ผู้รับบริการ : <b>{patientName || "-"}</b></div>
            <div>HN : {hnText || "-"}</div>
            <div>Encounter : {encounterNo || "-"}</div>
            <div>วันที่ส่งตรวจ : {labRequestDoc.at}</div>
            <div>แพทย์ผู้สั่ง : {doctorName || "-"}</div>
            <div>ความเร่งด่วน : ปกติ (Routine)</div>
          </div>
          <div style={{ border: "1px solid #D5DEE2", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "0.4fr 1.5fr 1.6fr 1.2fr 1.6fr", gap: 8, padding: "8px 10px", background: "#E8F3F7", fontWeight: 700, fontSize: 12 }}>
              <span>ลำดับ</span>
              <span>หลอด / ภาชนะ</span>
              <span>รายการตรวจ</span>
              <span>แผนก</span>
              <span>ส่งตรวจที่</span>
            </div>
            {labRequestDoc.rows.map((s, i) => (
              <div key={String(s.id)} style={{ display: "grid", gridTemplateColumns: "0.4fr 1.5fr 1.6fr 1.2fr 1.6fr", gap: 8, padding: "7px 10px", borderTop: "1px solid #E3E9EC", fontSize: 13 }}>
                <span>{i + 1}</span>
                <span>
                  <span style={{ display: "inline-block", width: 14, height: 14, borderRadius: 7, border: "1px solid #243036", verticalAlign: -2, marginRight: 6, background: s.color || "#CBD3D7" }} />
                  {s.specimenName || s.barcode}
                </span>
                <span>{s.labTests || "-"}</span>
                <span>{s.labDivision || "-"}</span>
                <span>{s.vendorName || s.labType || "-"}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: "auto", paddingTop: 28, display: "flex", justifyContent: "space-between", fontSize: 13, color: "#3C4C52" }}>
            <span style={{ textAlign: "center" }}>__________________<br />ผู้เก็บสิ่งส่งตรวจ</span>
            <span style={{ textAlign: "center" }}>__________________<br />{doctorName || "-"} · แพทย์ผู้สั่ง</span>
          </div>
        </div>
      </ModalV4>
    );
  };

  const renderDrugDoc = () => {
    if (!drugDoc) return null;
    const isHomeDoc = drugDoc.mode === "home";
    return (
      <ModalV4 title={isHomeDoc ? "ใบยากลับบ้าน · A4" : "ฉลากยา"} width={840} onClose={() => setDrugDoc(null)}>
        {isHomeDoc ? (
          <div style={{ margin: "0 auto", boxSizing: "border-box", width: "100%", maxWidth: 794, aspectRatio: "210 / 297", padding: "44px 48px", border: "1px solid #CBD3D7", background: "#fff", color: "#1D2A30", fontSize: 14, lineHeight: 1.6, display: "flex", flexDirection: "column" }}>
            <div style={{ borderBottom: "2px solid #3FA3BB", paddingBottom: 10, marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#0B5F68" }}>Longevity &amp; Wellness</div>
                <div style={{ fontSize: 13, color: "#5B6C73" }}>คลินิกเวชกรรม</div>
              </div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>
                ใบยากลับบ้าน
                <div style={{ fontSize: 12, fontWeight: 400, textAlign: "right", color: "#5B6C73" }}>TAKE-HOME MEDICATION</div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 16px", marginBottom: 14, fontSize: 13 }}>
              <div>ผู้รับบริการ : <b>{patientName || "-"}</b></div>
              <div>HN : {hnText || "-"}</div>
              <div>Encounter : {encounterNo || "-"}</div>
              <div>วันที่จ่ายยา : {drugDoc.at}</div>
              <div>แพทย์ผู้สั่ง : {doctorName || "-"}</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14, padding: "12px 0", borderTop: "1px solid #E3E9EC" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{drugDoc.order.name}</div>
                <div>วิธีใช้ : {drugDoc.order.orderStatus || "-"}</div>
              </div>
            </div>
            <div style={{ marginTop: "auto", paddingTop: 24, display: "flex", justifyContent: "space-between", fontSize: 13, color: "#3C4C52" }}>
              <span>หากมีอาการแพ้หรือผิดปกติ ติดต่อคลินิก</span>
              <span style={{ textAlign: "center" }}>__________________<br />ผู้จ่ายยา</span>
            </div>
          </div>
        ) : (
          <div style={{ padding: 16, display: "flex", flexWrap: "wrap", gap: 14, justifyContent: "center", background: "#EEF3F4" }}>
            <div style={{ width: 360, boxSizing: "border-box", aspectRatio: "80 / 50", padding: "12px 14px", background: "#fff", border: "1px solid #9AA8AE", borderRadius: 6, fontSize: 12, lineHeight: 1.45, color: "#1D2A30", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #3FA3BB", paddingBottom: 3, fontSize: 11 }}>
                <b style={{ color: "#0B5F68" }}>Longevity &amp; Wellness · คลินิกเวชกรรม</b>
                <span>{drugDoc.at}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                <span><b>{patientName || "-"}</b></span>
                <span>HN {hnText || "-"}</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, marginTop: 3 }}>{drugDoc.order.name}</div>
              <div>{drugDoc.order.orderStatus || "-"}</div>
              <div style={{ marginTop: "auto", display: "flex", justifyContent: "space-between", fontSize: 10, color: "#5B6C73" }}>
                <span>HN {hnText || "-"}</span>
                <span>{doctorName || "-"}</span>
              </div>
            </div>
          </div>
        )}
      </ModalV4>
    );
  };

  const renderDeliveryDoc = () => {
    if (!deliveryDoc) return null;
    const key = String(deliveryDoc.order.id);
    const delivery = drugDelivery[key] || { mode: "ship", addr: "", phone: "", courier: COURIERS[0], trk: "" };
    return (
      <ModalV4 title="ตัวอย่างใบจัดส่งยา" width={840} onClose={() => setDeliveryDoc(null)}>
        <div style={{ margin: "0 auto", maxWidth: 794, aspectRatio: "210 / 297", boxSizing: "border-box", padding: "36px 40px", display: "flex", flexDirection: "column", color: "#1F2A30" }}>
          <div style={{ textAlign: "center", borderBottom: "2px solid #3FA3BB", paddingBottom: 10 }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>Longevity &amp; Wellness</div>
            <div style={{ fontSize: 12, color: "#5B6C73" }}>คลินิกเวชกรรม · ห้องยา</div>
          </div>
          <div style={{ textAlign: "center", fontSize: 18, fontWeight: 700, margin: "14px 0 12px" }}>ใบจัดส่งยา</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
            <div style={{ flex: "1 1 280px", border: "1px solid #1F2A30", padding: "10px 12px" }}>
              <div style={{ fontSize: 12, color: "#5B6C73" }}>ผู้ส่ง</div>
              <div style={{ fontWeight: 700 }}>Longevity &amp; Wellness · ห้องยา</div>
            </div>
            <div style={{ flex: "1 1 280px", border: "2px solid #1F2A30", padding: "10px 12px" }}>
              <div style={{ fontSize: 12, color: "#5B6C73" }}>ผู้รับ</div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{patientName || "-"}</div>
              <div style={{ fontSize: 13 }}>โทร {delivery.phone || "-"}</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>{delivery.addr || "-"}</div>
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 10, margin: "14px 0", padding: "10px 12px", background: "#F4F8FA", borderRadius: 4 }}>
            <div>
              <div style={{ fontSize: 12, color: "#5B6C73" }}>ขนส่ง · Tracking no.</div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{delivery.courier} · {delivery.trk}</div>
            </div>
          </div>
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            HN {hnText || "-"} · วันที่ส่ง {deliveryDoc.at}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#EAF0F2", textAlign: "left" }}>
                <th style={{ padding: "6px 8px" }}>#</th>
                <th style={{ padding: "6px 8px" }}>รายการ</th>
                <th style={{ padding: "6px 8px" }}>Lot</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderTop: "1px solid #E3E9EC" }}>
                <td style={{ padding: "6px 8px" }}>1</td>
                <td style={{ padding: "6px 8px" }}>{deliveryDoc.order.name}</td>
                <td style={{ padding: "6px 8px" }}>-</td>
              </tr>
            </tbody>
          </table>
          <div style={{ marginTop: 14, padding: "8px 12px", border: "1px dashed #5B6C73", fontSize: 12 }}>
            ยาในพัสดุนี้เป็นของผู้รับตามชื่อข้างต้นเท่านั้น · เก็บให้พ้นแสงและความร้อน · หากพัสดุเสียหายหรือยาผิดปกติ โปรดติดต่อคลินิก
          </div>
          <div style={{ marginTop: "auto", display: "flex", justifyContent: "space-between", gap: 18, paddingTop: 26, fontSize: 12 }}>
            <div style={{ textAlign: "center", minWidth: 200 }}>
              <div style={{ borderBottom: "1px solid #1F2A30", height: 28 }} />
              เภสัชกร / ผู้จัดส่ง
            </div>
            <div style={{ textAlign: "center", minWidth: 200 }}>
              <div style={{ borderBottom: "1px solid #1F2A30", height: 28 }} />
              เจ้าหน้าที่ขนส่งรับพัสดุ
            </div>
          </div>
        </div>
      </ModalV4>
    );
  };

  /* ==================== RENDER หลัก (โครงสร้างเหมือนแบบ: h1 "ผลตรวจ" → tabs → เนื้อหาแต่ละแท็บ) ==================== */
  const tabDefs: { key: TabKey; label: string; badge: string; badgeLabel: string; over: boolean }[] = [
    {
      key: "lab",
      label: "Lab",
      badge: waitSpecCount + pendLabRows.length > 0 ? `รอ ${waitSpecCount + pendLabRows.length}` : labResults.length > 0 ? "✓" : "-",
      badgeLabel: waitSpecCount + pendLabRows.length > 0 ? `รอดำเนินการ ${waitSpecCount + pendLabRows.length} รายการ` : "ครบแล้ว",
      over: false,
    },
    {
      key: "img",
      label: "Imaging",
      badge: waitImgCount > 0 ? `รอ ${waitImgCount}` : imagingList.length > 0 ? "✓" : "-",
      badgeLabel: waitImgCount > 0 ? `รอทำ ${waitImgCount} รายการ` : "ครบแล้ว",
      over: false,
    },
    {
      key: "proc",
      label: "Treatment",
      badge: waitProcCount > 0 ? `รอ ${waitProcCount}` : procList.length > 0 ? "✓" : "-",
      badgeLabel: waitProcCount > 0 ? `รอทำ ${waitProcCount} รายการ` : "ครบแล้ว",
      over: false,
    },
    {
      key: "drug",
      label: "Drug",
      badge: waitDrugCount > 0 ? `รอ ${waitDrugCount}` : drugList.length > 0 ? "✓" : "-",
      badgeLabel: waitDrugCount > 0 ? `รอดำเนินการ ${waitDrugCount} รายการ` : "ครบแล้ว",
      over: false,
    },
  ];

  return (
    <div className="hv4-workspace" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {cardErrorText && (
        <AlertV4 tone="danger" onClose={dismissError}>
          {cardErrorText}
        </AlertV4>
      )}

      {statusMsg && (
        <div role="status" style={{ padding: "12px 16px", borderRadius: 4, background: "#E3F5E8", border: "1px solid #9AD2A8", color: "#17592B", fontSize: 15 }}>
          {statusMsg}
        </div>
      )}
      {entErr && (
        <div role="alert" style={{ padding: "12px 16px", borderRadius: 4, background: "#FBE0DD", border: "1px solid #D9362B", color: "#5A120B", fontSize: 15 }}>
          {entErr}
        </div>
      )}

      <h1 className="hv4-h1" style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>ผลตรวจ</h1>

      <div role="tablist" style={{ display: "flex", gap: 0, borderBottom: "2px solid #3FA3BB" }}>
        {tabDefs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => {
              setTab(t.key);
              setStatusMsg(null);
              setEntErr(null);
            }}
            style={{
              minHeight: 40,
              padding: "0 18px",
              border: 0,
              borderBottom: `4px solid ${tab === t.key ? "#3FA3BB" : "transparent"}`,
              background: tab === t.key ? "#DDF1F6" : "transparent",
              fontSize: 15,
              fontWeight: tab === t.key ? 700 : 500,
              color: "#243036",
              marginBottom: -2,
              cursor: "pointer",
            }}
          >
            {t.label}
            <span
              role="img"
              aria-label={t.badgeLabel}
              style={{
                marginLeft: 8,
                minWidth: 24,
                height: 24,
                padding: "0 8px",
                boxSizing: "border-box",
                borderRadius: 12,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 700,
                background: t.over ? "#D9362B" : t.badge.toString().startsWith("รอ") ? "#F2B01E" : t.badge === "-" ? "#EEF3F4" : "#1E8E3E",
                color: t.badge === "✓" ? "#fff" : t.badge === "-" ? "#243036" : t.over ? "#fff" : t.badge.toString().startsWith("รอ") ? "#3F2A00" : "#fff",
              }}
            >
              {t.badge}
            </span>
          </button>
        ))}
      </div>

      {tab === "lab" && renderLabTab()}
      {tab === "img" && renderImagingTab()}
      {tab === "proc" && renderProcTab()}
      {tab === "drug" && renderDrugTab()}

      {trendLabKey !== "" && renderTrendModal()}
      {labRequestDoc && renderLabRequestDoc()}
      {drugDoc && renderDrugDoc()}
      {deliveryDoc && renderDeliveryDoc()}

      {authCollectOpen && (
        <ModalV4 title="ยืนยันการเก็บสิ่งส่งตรวจ (Specimen Collection)" width={440} onClose={() => setAuthCollectOpen(false)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 14, color: "#51646A" }}>กรุณาระบุรหัสผ่านผู้เก็บสิ่งส่งตรวจเพื่อยืนยันรายการ (V3 Authentication)</div>
            <div>
              <label className="hv4-label">Username ผู้เก็บ</label>
              <input className="hv4-input" value={collectUsername} onChange={(e) => setCollectUsername(e.target.value)} placeholder="Username ผู้เก็บสิ่งส่งตรวจ" />
            </div>
            <div>
              <label className="hv4-label">Password *</label>
              <input type="password" className="hv4-input" value={collectPassword} onChange={(e) => setCollectPassword(e.target.value)} placeholder="รหัสผ่านผู้เก็บ" />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
              <ButtonV4 onClick={handleConfirmCollect}>ยืนยันการเก็บสิ่งส่งตรวจ</ButtonV4>
              <ButtonV4 variant="ghost" onClick={() => setAuthCollectOpen(false)}>
                ยกเลิก
              </ButtonV4>
            </div>
          </div>
        </ModalV4>
      )}
    </div>
  );
};

export default CardResultSummaryV4;
